import React, {Fragment, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {useSearchParams} from 'react-router-dom';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import clsx from 'clsx';
import {
    ArrowLeft,
    ChevronDown,
    CreditCard,
    ExternalLink,
    FlaskConical,
    KeyRound,
    LayoutDashboard,
    Loader2,
    LogOut,
    Pencil,
    Plus,
    Package,
    Save,
    Search,
    ShoppingBag,
    SlidersHorizontal,
    Sparkles,
    Tag,
    
    Wrench,
    Trash2,
    Users,
    X,
} from 'lucide-react';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {useTenant} from '@/shared/hooks/useTenant';
import {
    decodeJwtToken,
    getOrganizationIdFromTokenPayload,
    isOrganizationUser,
    isUserAdmin,
} from '@/shared/utils/tokenUtils';
import {ADMIN_RETURN_STATE_KEY} from '@/shared/constants/providerSwitch';
import {useProviderConsoleSwitch} from '@/modules/provider-console/hooks/useProviderConsoleSwitch';
import DashboardLayout from '@/shared/layout/DashboardLayout';
import Button from '@/shared/components/Button';
import AdminSearch from '@/modules/admin/components/AdminSearch';
import {PasswordReset} from '@/modules/admin/components/PasswordReset';
import {RejectedEmails} from '@/modules/admin/components/RejectedEmails';
import {BioMatchManager} from '@/modules/biomatch';
import KitCostSettings from '@/modules/admin/components/KitCostSettings';
import BiomarkersAdminPage from '@/modules/admin/components/BiomarkersAdminPage';
import OrganizationCatalog from '@/modules/admin/components/OrganizationCatalog';
import OhdLabelMapping from '@/modules/admin/components/OhdLabelMapping';
import ReferenceRangesManager from '@/modules/admin/components/ReferenceRangesManager';
import ProviderApiKeys from '@/modules/admin/components/ProviderApiKeys';
import StorefrontProductBuilder from '@/modules/admin/components/StorefrontProductBuilder';
import AdminCouponsManager from '@/modules/admin/components/AdminCouponsManager';
import AdminOrdersSection from '@/modules/admin/views/AdminOrdersSection';
import {EmployeeHealthDashboard, OhdModeControl} from '@/modules/ohd';
import AddOrganizationModal from '@/modules/admin/components/AddOrganizationModal';
import LabTestPricing from '@/modules/admin/components/LabTestPricing';
import GlobalOverview from '@/modules/admin/components/GlobalOverview';
import OrganizationProviderConsole from '@/modules/admin/components/OrganizationProviderConsole';
import AdminPatientSearchModal from '@/modules/admin/components/AdminPatientSearchModal';
import {AdminNotificationProvider} from '@/modules/admin/contexts/AdminNotificationContext';
import AdminFortisIntegration from '@/modules/admin/AdminFortisIntegrationView';
import {AdminNotificationBanner} from '@/modules/admin/components/AdminNotificationBanner';
import type {
    AdminPatientSearchResult,
    OrganizationProvider,
    OrganizationSearchResult,
    OrganizationProviderModulesInput,
    ProviderConsoleModuleInput,
    ProviderLoginUser,
    ProviderRoleRow,
    ProviderUserRoleRow,
} from '@/shared/types/api';
import AdminService from '@/modules/admin/services/adminService';
import {getBaseDomain} from '@/shared/utils/domain';
import {buildAdminOrigin, getAdminHost} from '@/shared/utils/adminHost';
import {ProviderConsoleModuleRegistry} from '@mld/shared/constants/providerConsoleAccess';

interface Props {
    setPinnedMetrics?: (value: any) => void;
}

type AdminPage =
    | 'global'
    | 'lab-pricing'
    | 'biomatch'
    | 'global-products'
    | 'kit-costs'
    | 'loinc-codes'
    | 'biomarker-display-rules'
    | 'coupons'
    | 'orders'
    | 'password-resets'
    | 'fortis-integration'
    | 'org-detail';
type OrgDetailTab =
    | 'modules'
    | 'providers'
    | 'subdomain'
    | 'logins'
    | 'tests-catalog'
    | 'biomatch'
    | 'ref-ranges'
    | 'api-keys';
type OrgConsoleTab =
    | 'dashboard'
    | 'search'
    | 'password-resets'
    | 'rejected-emails'
    | 'catalog'
    | 'recommendations'
    | 'storefront-products'
    | 'api-keys';
type PatientSearchSeed = {
    userId?: number | null;
    firstName?: string | null;
    lastName?: string | null;
    birthday?: string | null;
    organizationName?: string | null;
    providerName?: string | null;
};
type PatientSearchPayload = PatientSearchSeed & {
    userId?: number | null;
    organizationId?: number | null;
    organizationName?: string | null;
    providerId?: number | null;
    providerName?: string | null;
};
type StatusMessage = {type: 'success' | 'error' | ''; text: string};
type ProviderUserFormState = {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    role_id: number | null;
};
type ProviderUserEditState = {id: number; form: ProviderUserFormState};

type HostInfo = {
    hostname: string;
    baseDomain: string;
    protocol: string;
    port: string;
    isDashboardHost: boolean;
};

const isIpHost = (hostname: string): boolean => /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);

const buildHostInfo = (): HostInfo => {
    if (typeof window === 'undefined') {
        return {hostname: '', baseDomain: '', protocol: '', port: '', isDashboardHost: true};
    }
    const {hostname, protocol, port} = window.location;
    const baseDomain = getBaseDomain(hostname);
    const isLocalHost = hostname === 'localhost' || isIpHost(hostname);
    const adminHost = getAdminHost(hostname);
    const isDashboardHost = isLocalHost || hostname === adminHost;
    return {hostname, baseDomain, protocol, port, isDashboardHost};
};

const buildAdminOriginFromHost = (hostInfo: HostInfo): string => {
    if (!hostInfo.hostname) return '';
    return buildAdminOrigin(hostInfo);
};

const getOrganizationLabel = (org: OrganizationSearchResult | null): string => {
    if (!org) return 'Organization';
    return org.display_name?.trim() || org.name || 'Organization';
};

const getInitials = (value: string): string => {
    const parts = value
        .split(' ')
        .map((part) => part.trim())
        .filter(Boolean);
    if (parts.length === 0) return 'NA';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const formatPhoneInput = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (!digits) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const normalizePhoneForSave = (value: string): string => value.replace(/\D/g, '');

const getDefaultProviderRoleId = (roles: ProviderRoleRow[] | undefined): number | null => {
    if (!roles || roles.length === 0) return null;
    const preferred = roles.find((role) => role.is_default);
    return preferred?.id ?? roles[0]?.id ?? null;
};

const filterOrganizations = (orgs: OrganizationSearchResult[], query: string): OrganizationSearchResult[] => {
    const term = query.trim().toLowerCase();
    if (!term) return orgs;
    return orgs.filter((org) => {
        const haystack = [org.name, org.display_name, org.subdomain].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(term);
    });
};

const Admin: React.FC<Props> = ({setPinnedMetrics = () => {}}) => {
    const queryClient = useQueryClient();
    const {user, logout} = useAuth();
    const {organization: tenantOrganization, theme: tenantTheme} = useTenant();
    const [searchParams, setSearchParams] = useSearchParams();
    const orgIdParam = searchParams.get('orgId');
    const orgFilterParam = searchParams.get('orgFilter');
    const orgDetailTabParam = searchParams.get('tab');
    const orgConsoleOverride = searchParams.get('orgConsole') === '1';
    const initialPage = orgIdParam ? 'org-detail' : 'global';
    const [activePage, setActivePage] = useState<AdminPage>(initialPage);
    const [selectedOrgId, setSelectedOrgId] = useState<number | null>(() => {
        const parsed = Number(orgIdParam);
        return Number.isFinite(parsed) ? parsed : null;
    });
    const [orgFilter, setOrgFilter] = useState('');
    const [spotlightQuery, setSpotlightQuery] = useState('');
    const [spotlightOpen, setSpotlightOpen] = useState(false);
    const [addOrgModalOpen, setAddOrgModalOpen] = useState(false);
    const [patientSearchOpen, setPatientSearchOpen] = useState(false);
    const [patientSearchSeed, setPatientSearchSeed] = useState<PatientSearchSeed | null>(null);
    const [fallbackOrg, setFallbackOrg] = useState<OrganizationSearchResult | null>(null);
    const [orgDetailTab, setOrgDetailTab] = useState<OrgDetailTab>('modules');
    const [orgConsoleTab, setOrgConsoleTab] = useState<OrgConsoleTab>('dashboard');
    const [ohdMappingModalOpen, setOhdMappingModalOpen] = useState(false);
    const [orgConsoleProviders, setOrgConsoleProviders] = useState<OrganizationProvider[]>([]);
    const [orgConsoleProvidersLoading, setOrgConsoleProvidersLoading] = useState(false);
    const [orgConsoleOrganizationModules, setOrgConsoleOrganizationModules] = useState<ProviderConsoleModuleInput[]>(
        []
    );
    const [orgConsoleProviderModules, setOrgConsoleProviderModules] = useState<
        Record<number, ProviderConsoleModuleInput[]>
    >({});
    const [orgConsoleProviderUsers, setOrgConsoleProviderUsers] = useState<ProviderLoginUser[]>([]);
    const [orgConsoleProviderRoles, setOrgConsoleProviderRoles] = useState<ProviderRoleRow[]>([]);
    const [orgConsoleProviderUserRoles, setOrgConsoleProviderUserRoles] = useState<ProviderUserRoleRow[]>([]);
    const [orgConsoleSelectedProviderId, setOrgConsoleSelectedProviderId] = useState<number | null>(null);
    const [orgConsoleProviderViewActive, setOrgConsoleProviderViewActive] = useState(false);
    const [orgConsoleProviderModulesSaving, setOrgConsoleProviderModulesSaving] = useState(false);
    const [orgConsoleProviderModulesMessage, setOrgConsoleProviderModulesMessage] = useState<StatusMessage>({
        type: '',
        text: '',
    });
    const [orgConsoleProviderUserAddOpen, setOrgConsoleProviderUserAddOpen] = useState(false);
    const [orgConsoleProviderUserForm, setOrgConsoleProviderUserForm] = useState<ProviderUserFormState>({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role_id: null,
    });
    const [orgConsoleProviderUserEdit, setOrgConsoleProviderUserEdit] = useState<ProviderUserEditState | null>(null);
    const [orgConsoleProviderUserSaving, setOrgConsoleProviderUserSaving] = useState(false);
    const [orgConsoleProviderUserDeletingId, setOrgConsoleProviderUserDeletingId] = useState<number | null>(null);
    const [orgConsoleProviderUserMessage, setOrgConsoleProviderUserMessage] = useState<StatusMessage>({
        type: '',
        text: '',
    });
    const [orgConsoleProviderUserEditMessage, setOrgConsoleProviderUserEditMessage] = useState<StatusMessage>({
        type: '',
        text: '',
    });
    const [orgConsoleProviderNameEditing, setOrgConsoleProviderNameEditing] = useState(false);
    const [orgConsoleProviderNameDraft, setOrgConsoleProviderNameDraft] = useState('');
    const [orgConsoleProviderNameSaving, setOrgConsoleProviderNameSaving] = useState(false);
    const [orgConsoleProviderNameMessage, setOrgConsoleProviderNameMessage] = useState<StatusMessage>({
        type: '',
        text: '',
    });
    const [orgConsoleShowInactive, setOrgConsoleShowInactive] = useState(false);
    const [orgConsoleAddProviderOpen, setOrgConsoleAddProviderOpen] = useState(false);
    const [orgConsoleAddProviderName, setOrgConsoleAddProviderName] = useState('');
    const [orgConsoleAddProviderNpi, setOrgConsoleAddProviderNpi] = useState('');
    const [orgConsoleAddProviderSaving, setOrgConsoleAddProviderSaving] = useState(false);
    const [orgConsoleAddProviderMessage, setOrgConsoleAddProviderMessage] = useState<StatusMessage>({
        type: '',
        text: '',
    });
    const [orgConsoleDeleteProviderOpen, setOrgConsoleDeleteProviderOpen] = useState(false);
    const [orgConsoleDeleteProviderTarget, setOrgConsoleDeleteProviderTarget] = useState<number | null>(null);
    const [orgConsoleDeleteProviderSaving, setOrgConsoleDeleteProviderSaving] = useState(false);
    const [orgConsoleDeleteProviderMessage, setOrgConsoleDeleteProviderMessage] = useState<StatusMessage>({
        type: '',
        text: '',
    });
    const spotlightInputRef = useRef<HTMLInputElement | null>(null);
    const userMenuRef = useRef<HTMLDivElement | null>(null);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const orgConsoleSelectedProviderIdRef = useRef<number | null>(null);
    const hostInfo = useMemo(buildHostInfo, []);
    const isDashboardHost = hostInfo.isDashboardHost && !orgConsoleOverride;
    const adminOrigin = useMemo(() => buildAdminOriginFromHost(hostInfo), [hostInfo]);
    const isAdminUser = isUserAdmin(user?.token) || isOrganizationUser(user?.token);
    const {
        switchToProvider: handleViewProviderConsole,
        switchingProviderId: orgConsoleProviderSwitching,
        error: providerSwitchError,
    } = useProviderConsoleSwitch();
    useEffect(() => {
        if (providerSwitchError) {
            setOrgConsoleProviderUserMessage({type: 'error', text: providerSwitchError});
        }
    }, [providerSwitchError]);
    const orgSwitchAttemptRef = useRef(false);

    const organizationsQuery = useQuery({
        queryKey: ['admin-organizations'],
        queryFn: () => AdminService.listOrganizations(2000),
        enabled: isDashboardHost,
    });

    const organizations = useMemo(
        () => (organizationsQuery.data ?? []) as OrganizationSearchResult[],
        [organizationsQuery.data]
    );
    const organizationsById = useMemo(() => {
        return organizations.reduce((acc, org) => {
            acc.set(org.id, org);
            return acc;
        }, new Map<number, OrganizationSearchResult>());
    }, [organizations]);

    useEffect(() => {
        orgConsoleSelectedProviderIdRef.current = orgConsoleSelectedProviderId;
    }, [orgConsoleSelectedProviderId]);

    const refreshOrgConsoleProviders = useCallback(async () => {
        if (isDashboardHost) return;
        if (!tenantOrganization?.id) return;
        setOrgConsoleProvidersLoading(true);
        try {
            const data = await AdminService.getOrganizationProviderConsole(tenantOrganization.id);
            const providers = data.providers ?? [];
            const providerModules = Object.entries(data.provider_modules ?? {}).reduce<
                Record<number, ProviderConsoleModuleInput[]>
            >((acc, [providerId, modules]) => {
                acc[Number(providerId)] = modules ?? [];
                return acc;
            }, {});
            setOrgConsoleProviders(providers);
            setOrgConsoleOrganizationModules(data.organization_modules ?? []);
            setOrgConsoleProviderModules(providerModules);
            setOrgConsoleProviderUsers(data.provider_users ?? []);
            setOrgConsoleProviderRoles(data.provider_roles ?? []);
            setOrgConsoleProviderUserRoles(data.provider_user_roles ?? []);

            const selectedId = orgConsoleSelectedProviderIdRef.current;
            if (selectedId && !providers.some((provider) => provider.id === selectedId)) {
                setOrgConsoleSelectedProviderId(null);
                setOrgConsoleProviderViewActive(false);
            }
        } catch {
            setOrgConsoleProviders([]);
            setOrgConsoleOrganizationModules([]);
            setOrgConsoleProviderModules({});
            setOrgConsoleProviderUsers([]);
            setOrgConsoleProviderRoles([]);
            setOrgConsoleProviderUserRoles([]);
        } finally {
            setOrgConsoleProvidersLoading(false);
        }
    }, [isDashboardHost, tenantOrganization?.id]);

    useEffect(() => {
        void refreshOrgConsoleProviders();
    }, [refreshOrgConsoleProviders]);

    useEffect(() => {
        if (!orgIdParam) {
            setSelectedOrgId(null);
            return;
        }
        const parsed = Number(orgIdParam);
        if (!Number.isFinite(parsed)) return;
        setSelectedOrgId(parsed);
    }, [orgIdParam]);

    // ── URL ↔ activePage sync ────────────────────────────────────────
    // React Router's setSearchParams() and React's setActivePage() don't
    // batch together — setSearchParams triggers a re-render before
    // setActivePage flushes. Without refs, this effect would see stale
    // activePage state and override intentional sidebar navigation.
    // Solution: handleNavChange sets activePageRef synchronously BEFORE
    // calling setSearchParams, and this effect reads the ref (always
    // current) instead of the state value. prevOrgIdParamRef prevents
    // re-triggering when unrelated query params change.
    const prevOrgIdParamRef = useRef(orgIdParam);
    const activePageRef = useRef(activePage);
    activePageRef.current = activePage;
    useEffect(() => {
        if (!isDashboardHost) return;
        const prev = prevOrgIdParamRef.current;
        prevOrgIdParamRef.current = orgIdParam;
        if (orgIdParam && !prev) {
            setActivePage('org-detail');
        }
        if (!orgIdParam && prev && activePageRef.current === 'org-detail') {
            setActivePage('global');
        }
    }, [isDashboardHost, orgIdParam]);

    useEffect(() => {
        if (!orgIdParam) return;
        try {
            const raw = sessionStorage.getItem(ADMIN_RETURN_STATE_KEY);
            if (!raw) return;
            const state = JSON.parse(raw) as {
                orgId?: number | string;
                orgDetailTab?: OrgDetailTab;
                orgFilter?: string;
            };
            const stateOrgId = Number(state?.orgId);
            if (!Number.isFinite(stateOrgId) || stateOrgId !== Number(orgIdParam)) {
                return;
            }
            if (state.orgFilter != null) setOrgFilter(state.orgFilter);
            if (state.orgDetailTab) setOrgDetailTab(state.orgDetailTab);
        } catch {
            // Ignore malformed state
        } finally {
            try {
                sessionStorage.removeItem(ADMIN_RETURN_STATE_KEY);
            } catch {
                // Ignore storage errors
            }
        }
    }, [orgIdParam]);

    useEffect(() => {
        if (!orgIdParam) return;
        if (orgFilterParam != null) setOrgFilter(orgFilterParam);
        if (orgDetailTabParam) {
            const nextTab = orgDetailTabParam as OrgDetailTab;
            const allowedTabs: OrgDetailTab[] = [
                'modules',
                'providers',
                'subdomain',
                'logins',
                'tests-catalog',
                'ref-ranges',
                'api-keys',
            ];
            if (allowedTabs.includes(nextTab)) {
                setOrgDetailTab(nextTab);
            }
        }
    }, [orgDetailTabParam, orgFilterParam, orgIdParam]);

    useEffect(() => {
        if (isDashboardHost) return;
        if (!tenantOrganization?.id) return;
        if (!user?.token || !isAdminUser) return;
        if (orgSwitchAttemptRef.current) return;

        const payload = decodeJwtToken(user.token);
        const tokenOrgIdRaw = getOrganizationIdFromTokenPayload(payload);
        const tokenOrgId = tokenOrgIdRaw ? Number(tokenOrgIdRaw) : NaN;
        if (!Number.isFinite(tokenOrgId) || tokenOrgId === tenantOrganization.id) {
            return;
        }

        const attemptedKey = `org_switch_attempted_${tenantOrganization.id}`;
        try {
            if (sessionStorage.getItem(attemptedKey)) {
                return;
            }
            sessionStorage.setItem(attemptedKey, '1');
        } catch {
            // Ignore storage errors
        }

        orgSwitchAttemptRef.current = true;
        AdminService.createOrgSwitchToken(tenantOrganization.id)
            .then(async (response) => {
                // Exchange on current origin — sets refresh cookie scoped to base domain.
                await AdminService.exchangeOrgSwitchToken(response.token);
                window.location.replace('/admin?orgConsole=1');
            })
            .catch((error) => {
                console.error('Failed to realign org console session', error);
            });
    }, [isAdminUser, isDashboardHost, tenantOrganization?.id, user?.token]);

    useEffect(() => {
        if (!isDashboardHost) return;
        if (!selectedOrgId) {
            setFallbackOrg(null);
            return;
        }
        if (organizationsById.has(selectedOrgId)) {
            setFallbackOrg(null);
            return;
        }
        let isActive = true;
        AdminService.searchOrganizations(String(selectedOrgId))
            .then((results) => {
                if (!isActive) return;
                setFallbackOrg(results[0] ?? null);
            })
            .catch(() => {
                if (!isActive) return;
                setFallbackOrg(null);
            });
        return () => {
            isActive = false;
        };
    }, [isDashboardHost, organizationsById, selectedOrgId]);

    const selectedOrg = selectedOrgId ? (organizationsById.get(selectedOrgId) ?? fallbackOrg) : null;
    const filteredOrganizations = useMemo(
        () => filterOrganizations(organizations, orgFilter),
        [organizations, orgFilter]
    );
    const spotlightResults = useMemo(
        () => filterOrganizations(organizations, spotlightQuery).slice(0, 50),
        [organizations, spotlightQuery]
    );

    const handleSelectOrg = useCallback(
        (org: OrganizationSearchResult) => {
            setSelectedOrgId(org.id);
            setActivePage('org-detail');
            setOrgDetailTab('modules');
            const nextParams = new URLSearchParams(searchParams);
            nextParams.set('orgId', String(org.id));
            setSearchParams(nextParams, {replace: true});
        },
        [searchParams, setSearchParams]
    );

    const handleNavChange = useCallback(
        (page: AdminPage) => {
            activePageRef.current = page;
            setActivePage(page);
            if (page !== 'org-detail') {
                setSearchParams({});
            }
        },
        [setSearchParams]
    );

    const handleSpotlightOpen = useCallback(() => {
        setSpotlightQuery('');
        setSpotlightOpen(true);
    }, []);

    const handlePatientSearchOpen = useCallback(() => {
        setPatientSearchSeed(null);
        setPatientSearchOpen(true);
    }, []);

    const handleSpotlightSelect = useCallback(
        (org: OrganizationSearchResult) => {
            handleSelectOrg(org);
            setSpotlightOpen(false);
        },
        [handleSelectOrg]
    );

    useEffect(() => {
        if (!spotlightOpen) return;
        const input = spotlightInputRef.current;
        if (!input) return;
        input.focus();
    }, [spotlightOpen]);

    useEffect(() => {
        if (!isDashboardHost) return;
        const handler = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                handleSpotlightOpen();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [handleSpotlightOpen, isDashboardHost]);

    const openOrgConsoleForOrganization = useCallback(
        async (org: {id: number; subdomain?: string | null}, payload?: PatientSearchPayload) => {
            if (!org?.id || typeof window === 'undefined') return;
            try {
                const response = await AdminService.createOrgSwitchToken(org.id);
                const baseDomain = getBaseDomain(window.location.hostname);
                const adminHost = getAdminHost(window.location.hostname);
                const portSuffix = window.location.port ? `:${window.location.port}` : '';
                const targetHostname = org.subdomain ? `${org.subdomain}.${baseDomain}` : adminHost;
                const isDashboardTarget = targetHostname === adminHost;
                const redirectParams = new URLSearchParams();
                if (isDashboardTarget) {
                    redirectParams.set('orgConsole', '1');
                }
                if (payload) {
                    redirectParams.set('ps', JSON.stringify(payload));
                }
                const redirectPath = redirectParams.toString() ? `/admin?${redirectParams.toString()}` : '/admin';

                await AdminService.exchangeOrgSwitchToken(response.token);
                const targetUrl = `${window.location.protocol}//${targetHostname}${portSuffix}${redirectPath}`;
                window.location.assign(targetUrl);
            } catch (error) {
                console.error('Failed to open org console', error);
            }
        },
        []
    );

    const openOrgConsole = useCallback(async () => {
        if (!selectedOrg) return;
        await openOrgConsoleForOrganization(selectedOrg);
    }, [openOrgConsoleForOrganization, selectedOrg]);

    const handlePatientSelect = useCallback(
        (user: AdminPatientSearchResult) => {
            const org = user.primaryOrganization ?? user.organizations[0];
            if (!org?.id) {
                console.error('No organization found for selected patient');
                return;
            }
            const payload: PatientSearchPayload = {
                userId: user.id,
                firstName: user.firstName,
                lastName: user.lastName,
                birthday: user.birthday ?? null,
                organizationId: org.id,
                organizationName: org.displayName || org.name,
                providerId: user.primaryProvider?.id ?? null,
                providerName: user.primaryProvider?.name ?? null,
            };
            setPatientSearchOpen(false);
            openOrgConsoleForOrganization(org, payload);
        },
        [openOrgConsoleForOrganization]
    );

    const handleBackToAdmin = useCallback(() => {
        if (!adminOrigin || typeof window === 'undefined') return;
        const orgId = tenantOrganization?.id;
        const target = orgId ? `${adminOrigin}/admin?orgId=${orgId}` : `${adminOrigin}/admin`;
        window.location.assign(target);
    }, [adminOrigin, tenantOrganization?.id]);

    const userInfo = user?.userInfo as {firstName?: string; lastName?: string; email?: string} | undefined;
    const userName =
        [userInfo?.firstName, userInfo?.lastName].filter(Boolean).join(' ') || user?.username || 'Super Admin';
    const patientSearchParam = searchParams.get('ps');

    useEffect(() => {
        if (!patientSearchParam) return;
        try {
            const parsed = JSON.parse(patientSearchParam) as PatientSearchPayload;
            setPatientSearchSeed({
                userId: parsed.userId ?? null,
                firstName: parsed.firstName ?? null,
                lastName: parsed.lastName ?? null,
                birthday: parsed.birthday ?? null,
                organizationName: parsed.organizationName ?? null,
                providerName: parsed.providerName ?? null,
            });
            if (isDashboardHost) {
                setPatientSearchOpen(true);
            } else {
                setOrgConsoleProviderViewActive(false);
                setOrgConsoleTab('search');
            }
        } catch (error) {
            console.error('Failed to parse patient search payload', error);
        }
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('ps');
        setSearchParams(nextParams, {replace: true});
    }, [isDashboardHost, patientSearchParam, searchParams, setSearchParams]);

    useEffect(() => {
        if (!userMenuOpen) return;
        const handleClick = (event: MouseEvent) => {
            if (!userMenuRef.current) return;
            if (userMenuRef.current.contains(event.target as Node)) return;
            setUserMenuOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => {
            document.removeEventListener('mousedown', handleClick);
        };
    }, [userMenuOpen]);

    const handleLogout = useCallback(() => {
        setUserMenuOpen(false);
        logout();
    }, [logout]);

    const orgConsoleTabLabels: Record<OrgConsoleTab, string> = {
        dashboard: 'Organization Dashboard',
        search: 'Patient Search',
        'password-resets': 'Password Resets',
        'rejected-emails': 'Rejected Emails',
        catalog: 'Lab Tests Catalog',
        recommendations: 'BioMatch',
        'storefront-products': 'Storefront Products',
        'api-keys': 'API Keys',
    };

    // Provider API keys are super-admin-only on the BE (the admin/providers/
    // :id/api-keys endpoints 403 organization roles), so only surface the tab
    // to super-admins who entered this console via org-switch.
    const canManageProviderApiKeys = isUserAdmin(user?.token);
    const orgConsoleSecondaryTabs: Array<{id: OrgConsoleTab; label: string}> = [
        {id: 'rejected-emails', label: 'Rejected Emails'},
        {id: 'catalog', label: 'Lab Tests Catalog'},
        {id: 'recommendations', label: 'BioMatch'},
        {id: 'storefront-products', label: 'Storefront Products'},
        ...(canManageProviderApiKeys ? [{id: 'api-keys', label: 'API Keys'} as const] : []),
    ];

    const tenantOrgTitle = tenantOrganization?.display_name || tenantOrganization?.name || 'Organization';
    const orgConsoleLogoUrl = tenantTheme?.logo_url || '';
    const orgConsoleProviderCount = orgConsoleProviders.length;
    const orgConsoleActiveProviderCount = orgConsoleProviders.filter((p) => p.is_active !== false).length;
    const orgConsoleInactiveCount = orgConsoleProviders.filter((p) => p.is_active === false).length;
    const orgConsoleVisibleProviders = orgConsoleShowInactive
        ? orgConsoleProviders
        : orgConsoleProviders.filter((p) => p.is_active !== false);
    const orgConsoleSelectedProvider =
        orgConsoleProviders.find((provider) => provider.id === orgConsoleSelectedProviderId) ?? null;
    const orgConsoleSelectedProviderUsers = orgConsoleSelectedProvider
        ? orgConsoleProviderUsers.filter((user) => user.provider_id === orgConsoleSelectedProvider.id)
        : [];
    const orgConsoleProviderRoleNameById = useMemo(() => {
        return orgConsoleProviderRoles.reduce<Record<number, string>>((acc, role) => {
            acc[role.id] = role.name;
            return acc;
        }, {});
    }, [orgConsoleProviderRoles]);
    const orgConsoleSelectedProviderRolesByUser = useMemo(() => {
        if (!orgConsoleSelectedProvider) return {};
        return orgConsoleProviderUserRoles
            .filter((row) => row.provider_id === orgConsoleSelectedProvider.id)
            .reduce<Record<number, string[]>>((acc, row) => {
                const roleName = orgConsoleProviderRoleNameById[row.role_id];
                if (!roleName) return acc;
                acc[row.user_id] = acc[row.user_id] ? [...acc[row.user_id], roleName] : [roleName];
                return acc;
            }, {});
    }, [orgConsoleProviderRoleNameById, orgConsoleProviderUserRoles, orgConsoleSelectedProvider]);
    const orgConsoleSelectedProviderModuleMap = useMemo(() => {
        if (!orgConsoleSelectedProvider) return new Map<number, boolean>();
        const rows = orgConsoleProviderModules[orgConsoleSelectedProvider.id] ?? [];
        return new Map(rows.map((row) => [row.module_id, Boolean(row.enabled)]));
    }, [orgConsoleProviderModules, orgConsoleSelectedProvider]);
    const orgConsoleOrganizationModuleMap = useMemo(() => {
        return new Map(orgConsoleOrganizationModules.map((row) => [row.module_id, Boolean(row.enabled)]));
    }, [orgConsoleOrganizationModules]);
    const orgConsoleSelectedProviderRoles = useMemo(() => {
        if (!orgConsoleSelectedProvider) return [];
        return orgConsoleProviderRoles.filter((role) => role.provider_id === orgConsoleSelectedProvider.id);
    }, [orgConsoleProviderRoles, orgConsoleSelectedProvider]);
    const orgConsoleSelectedProviderRoleIdByUser = useMemo(() => {
        if (!orgConsoleSelectedProvider) return {};
        return orgConsoleProviderUserRoles
            .filter((row) => row.provider_id === orgConsoleSelectedProvider.id)
            .reduce<Record<number, number>>((acc, row) => {
                acc[row.user_id] = row.role_id;
                return acc;
            }, {});
    }, [orgConsoleProviderUserRoles, orgConsoleSelectedProvider]);
    const orgConsoleSelectedProviderDefaultRoleId = useMemo(() => {
        return getDefaultProviderRoleId(orgConsoleSelectedProviderRoles);
    }, [orgConsoleSelectedProviderRoles]);

    const handleOrgConsolePrimaryNav = useCallback((tab: OrgConsoleTab) => {
        setOrgConsoleTab(tab);
        setOrgConsoleProviderViewActive(false);
        setOrgConsoleSelectedProviderId(null);
    }, []);

    const handleOrgConsoleProviderSelect = useCallback((providerId: number) => {
        setOrgConsoleSelectedProviderId(providerId);
        setOrgConsoleProviderViewActive(true);
    }, []);

    useEffect(() => {
        if (!orgConsoleSelectedProvider) {
            setOrgConsoleProviderUserAddOpen(false);
            setOrgConsoleProviderUserEdit(null);
            setOrgConsoleProviderUserForm({
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
                role_id: null,
            });
            setOrgConsoleProviderUserMessage({type: '', text: ''});
            setOrgConsoleProviderUserEditMessage({type: '', text: ''});
            setOrgConsoleProviderNameEditing(false);
            setOrgConsoleProviderNameDraft('');
            setOrgConsoleProviderNameMessage({type: '', text: ''});
            setOrgConsoleDeleteProviderOpen(false);
            setOrgConsoleDeleteProviderTarget(null);
            setOrgConsoleDeleteProviderMessage({type: '', text: ''});
            return;
        }
        setOrgConsoleProviderUserAddOpen(false);
        setOrgConsoleProviderUserEdit(null);
        setOrgConsoleProviderUserForm({
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            role_id: orgConsoleSelectedProviderDefaultRoleId,
        });
        setOrgConsoleProviderUserMessage({type: '', text: ''});
        setOrgConsoleProviderUserEditMessage({type: '', text: ''});
        setOrgConsoleProviderNameEditing(false);
        setOrgConsoleDeleteProviderOpen(false);
        setOrgConsoleDeleteProviderTarget(null);
        setOrgConsoleDeleteProviderMessage({type: '', text: ''});
        setOrgConsoleProviderNameDraft(orgConsoleSelectedProvider.name ?? '');
        setOrgConsoleProviderNameMessage({type: '', text: ''});
    }, [orgConsoleSelectedProvider, orgConsoleSelectedProviderDefaultRoleId]);

    const handleOrgConsoleModuleToggle = useCallback(
        async (moduleId: number) => {
            if (!tenantOrganization?.id || !orgConsoleSelectedProvider) return;
            if (orgConsoleProviderModulesSaving) return;
            const providerId = orgConsoleSelectedProvider.id;
            const orgModuleEnabled = orgConsoleOrganizationModuleMap.get(moduleId) ?? false;
            const currentRows = orgConsoleProviderModules[providerId] ?? [];
            const index = currentRows.findIndex((row) => row.module_id === moduleId);
            const currentEnabled = index >= 0 ? Boolean(currentRows[index].enabled) : false;
            if (!orgModuleEnabled && !currentEnabled) return;

            const nextRows = [...currentRows];
            if (index >= 0) {
                nextRows[index] = {...nextRows[index], enabled: !currentEnabled};
            } else {
                nextRows.push({module_id: moduleId, enabled: true});
            }
            const nextProviderModules = {...orgConsoleProviderModules, [providerId]: nextRows};
            setOrgConsoleProviderModules(nextProviderModules);
            setOrgConsoleProviderModulesMessage({type: '', text: ''});

            setOrgConsoleProviderModulesSaving(true);
            try {
                const providerModulesPayload: OrganizationProviderModulesInput[] = Object.entries(
                    nextProviderModules
                ).map(([entryProviderId, modules]) => ({
                    provider_id: Number(entryProviderId),
                    modules,
                }));
                await AdminService.updateOrganizationProviderConsole(tenantOrganization.id, {
                    organization_modules: orgConsoleOrganizationModules,
                    provider_modules: providerModulesPayload,
                });
                setOrgConsoleProviderModulesMessage({type: 'success', text: 'Provider modules updated.'});
            } catch (error: any) {
                const message = error?.response?.data?.error || 'Failed to update provider modules.';
                setOrgConsoleProviderModulesMessage({type: 'error', text: message});
                setOrgConsoleProviderModules((prev) => ({...prev, [providerId]: currentRows}));
            } finally {
                setOrgConsoleProviderModulesSaving(false);
            }
        },
        [
            orgConsoleOrganizationModuleMap,
            orgConsoleOrganizationModules,
            orgConsoleProviderModules,
            orgConsoleProviderModulesSaving,
            orgConsoleSelectedProvider,
            tenantOrganization?.id,
        ]
    );

    const handleProviderUserFieldChange = useCallback(
        (field: keyof ProviderUserFormState, value: string | number | null) => {
            setOrgConsoleProviderUserForm((prev) => ({
                ...prev,
                [field]: field === 'phone' && typeof value === 'string' ? formatPhoneInput(value) : value,
            }));
        },
        []
    );

    const handleProviderUserEditFieldChange = useCallback(
        (field: keyof ProviderUserFormState, value: string | number | null) => {
            setOrgConsoleProviderUserEdit((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    form: {
                        ...prev.form,
                        [field]: field === 'phone' && typeof value === 'string' ? formatPhoneInput(value) : value,
                    },
                };
            });
        },
        []
    );

    const handleCreateProviderUser = useCallback(async () => {
        if (!tenantOrganization?.id || !orgConsoleSelectedProvider) return;
        setOrgConsoleProviderUserSaving(true);
        setOrgConsoleProviderUserMessage({type: '', text: ''});
        try {
            const payload = {
                first_name: orgConsoleProviderUserForm.first_name,
                last_name: orgConsoleProviderUserForm.last_name,
                email: orgConsoleProviderUserForm.email,
                phone: normalizePhoneForSave(orgConsoleProviderUserForm.phone),
                role_ids: orgConsoleProviderUserForm.role_id ? [orgConsoleProviderUserForm.role_id] : undefined,
            };
            await AdminService.createProviderUser(tenantOrganization.id, orgConsoleSelectedProvider.id, payload);
            setOrgConsoleProviderUserMessage({type: 'success', text: 'Provider user created.'});
            await refreshOrgConsoleProviders();
            setOrgConsoleProviderUserAddOpen(false);
            setOrgConsoleProviderUserForm({
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
                role_id: orgConsoleSelectedProviderDefaultRoleId,
            });
        } catch (error: any) {
            const message = error?.response?.data?.error || 'Failed to create provider user.';
            setOrgConsoleProviderUserMessage({type: 'error', text: message});
        } finally {
            setOrgConsoleProviderUserSaving(false);
        }
    }, [
        orgConsoleProviderUserForm,
        orgConsoleSelectedProvider,
        orgConsoleSelectedProviderDefaultRoleId,
        refreshOrgConsoleProviders,
        tenantOrganization?.id,
    ]);

    const handleStartProviderUserEdit = useCallback(
        (user: ProviderLoginUser) => {
            const roleId = orgConsoleSelectedProviderRoleIdByUser[user.id] ?? orgConsoleSelectedProviderDefaultRoleId;
            setOrgConsoleProviderUserEdit({
                id: user.id,
                form: {
                    first_name: user.first_name ?? '',
                    last_name: user.last_name ?? '',
                    email: user.email ?? '',
                    phone: formatPhoneInput(user.phone ?? ''),
                    role_id: roleId ?? null,
                },
            });
            setOrgConsoleProviderUserEditMessage({type: '', text: ''});
            setOrgConsoleProviderUserAddOpen(false);
        },
        [orgConsoleSelectedProviderDefaultRoleId, orgConsoleSelectedProviderRoleIdByUser]
    );

    const handleUpdateProviderUser = useCallback(async () => {
        if (!tenantOrganization?.id || !orgConsoleSelectedProvider || !orgConsoleProviderUserEdit) return;
        setOrgConsoleProviderUserSaving(true);
        setOrgConsoleProviderUserEditMessage({type: '', text: ''});
        try {
            const payload = {
                first_name: orgConsoleProviderUserEdit.form.first_name,
                last_name: orgConsoleProviderUserEdit.form.last_name,
                email: orgConsoleProviderUserEdit.form.email,
                phone: normalizePhoneForSave(orgConsoleProviderUserEdit.form.phone),
                role_ids: orgConsoleProviderUserEdit.form.role_id
                    ? [orgConsoleProviderUserEdit.form.role_id]
                    : undefined,
            };
            await AdminService.updateProviderUser(
                tenantOrganization.id,
                orgConsoleSelectedProvider.id,
                orgConsoleProviderUserEdit.id,
                payload
            );
            setOrgConsoleProviderUserEditMessage({type: 'success', text: 'Provider user updated.'});
            await refreshOrgConsoleProviders();
            setOrgConsoleProviderUserEdit(null);
        } catch (error: any) {
            const message = error?.response?.data?.error || 'Failed to update provider user.';
            setOrgConsoleProviderUserEditMessage({type: 'error', text: message});
        } finally {
            setOrgConsoleProviderUserSaving(false);
        }
    }, [orgConsoleProviderUserEdit, orgConsoleSelectedProvider, refreshOrgConsoleProviders, tenantOrganization?.id]);

    const handleDeleteProviderUser = useCallback(
        async (userId: number) => {
            if (!tenantOrganization?.id || !orgConsoleSelectedProvider) return;
            if (!window.confirm('Remove this provider user?')) return;
            setOrgConsoleProviderUserDeletingId(userId);
            setOrgConsoleProviderUserMessage({type: '', text: ''});
            try {
                await AdminService.deleteProviderUser(tenantOrganization.id, orgConsoleSelectedProvider.id, userId);
                setOrgConsoleProviderUserMessage({type: 'success', text: 'Provider user removed.'});
                await refreshOrgConsoleProviders();
                if (orgConsoleProviderUserEdit?.id === userId) {
                    setOrgConsoleProviderUserEdit(null);
                }
            } catch (error: any) {
                const message = error?.response?.data?.error || 'Failed to remove provider user.';
                setOrgConsoleProviderUserMessage({type: 'error', text: message});
            } finally {
                setOrgConsoleProviderUserDeletingId(null);
            }
        },
        [orgConsoleProviderUserEdit?.id, orgConsoleSelectedProvider, refreshOrgConsoleProviders, tenantOrganization?.id]
    );

    const handleProviderNameEdit = useCallback(() => {
        if (!orgConsoleSelectedProvider) return;
        setOrgConsoleProviderNameDraft(orgConsoleSelectedProvider.name ?? '');
        setOrgConsoleProviderNameEditing(true);
        setOrgConsoleProviderNameMessage({type: '', text: ''});
    }, [orgConsoleSelectedProvider]);

    const handleProviderNameCancel = useCallback(() => {
        if (!orgConsoleSelectedProvider) return;
        setOrgConsoleProviderNameDraft(orgConsoleSelectedProvider.name ?? '');
        setOrgConsoleProviderNameEditing(false);
        setOrgConsoleProviderNameMessage({type: '', text: ''});
    }, [orgConsoleSelectedProvider]);

    const handleProviderNameSave = useCallback(async () => {
        if (!tenantOrganization?.id || !orgConsoleSelectedProvider) return;
        const trimmed = orgConsoleProviderNameDraft.trim();
        if (!trimmed) {
            setOrgConsoleProviderNameMessage({type: 'error', text: 'Provider name is required.'});
            return;
        }
        if (trimmed === orgConsoleSelectedProvider.name) {
            setOrgConsoleProviderNameEditing(false);
            return;
        }
        setOrgConsoleProviderNameSaving(true);
        setOrgConsoleProviderNameMessage({type: '', text: ''});
        try {
            await AdminService.updateProviderName(tenantOrganization.id, orgConsoleSelectedProvider.id, trimmed);
            setOrgConsoleProviders((prev) =>
                prev.map((provider) =>
                    provider.id === orgConsoleSelectedProvider.id ? {...provider, name: trimmed} : provider
                )
            );
            setOrgConsoleProviderNameEditing(false);
            setOrgConsoleProviderNameMessage({type: 'success', text: 'Provider name updated.'});
        } catch (error: any) {
            const message = error?.response?.data?.error || 'Failed to update provider name.';
            setOrgConsoleProviderNameMessage({type: 'error', text: message});
        } finally {
            setOrgConsoleProviderNameSaving(false);
        }
    }, [orgConsoleProviderNameDraft, orgConsoleSelectedProvider, tenantOrganization?.id]);

    const handleAddProviderSubmit = useCallback(async () => {
        if (!tenantOrganization?.id) return;
        const trimmed = orgConsoleAddProviderName.trim();
        if (!trimmed) {
            setOrgConsoleAddProviderMessage({type: 'error', text: 'Provider name is required.'});
            return;
        }
        setOrgConsoleAddProviderSaving(true);
        setOrgConsoleAddProviderMessage({type: '', text: ''});
        try {
            const npi = orgConsoleAddProviderNpi.trim() || undefined;
            await AdminService.addProvider(tenantOrganization.id, trimmed, npi);
            setOrgConsoleAddProviderName('');
            setOrgConsoleAddProviderNpi('');
            setOrgConsoleAddProviderOpen(false);
            setOrgConsoleAddProviderMessage({type: 'success', text: 'Provider added.'});
            await refreshOrgConsoleProviders();
        } catch (error: any) {
            const message = error?.response?.data?.error || 'Failed to add provider.';
            setOrgConsoleAddProviderMessage({type: 'error', text: message});
        } finally {
            setOrgConsoleAddProviderSaving(false);
        }
    }, [orgConsoleAddProviderName, orgConsoleAddProviderNpi, tenantOrganization?.id, refreshOrgConsoleProviders]);

    const handleDeleteProviderSubmit = useCallback(async () => {
        if (!tenantOrganization?.id || !orgConsoleSelectedProvider || !orgConsoleDeleteProviderTarget) return;
        setOrgConsoleDeleteProviderSaving(true);
        setOrgConsoleDeleteProviderMessage({type: '', text: ''});
        try {
            const data = await AdminService.deleteProvider(
                tenantOrganization.id,
                orgConsoleSelectedProvider.id,
                orgConsoleDeleteProviderTarget
            );
            const warning = data.warning ? ` Warning: ${data.warning}` : '';
            setOrgConsoleDeleteProviderOpen(false);
            setOrgConsoleDeleteProviderTarget(null);
            setOrgConsoleDeleteProviderMessage({
                type: 'success',
                text: `Provider deleted and data reassigned.${warning}`,
            });
            setOrgConsoleProviderViewActive(false);
            setOrgConsoleSelectedProviderId(null);
            await refreshOrgConsoleProviders();
        } catch (error: any) {
            const message = error?.response?.data?.error || 'Failed to delete provider.';
            setOrgConsoleDeleteProviderMessage({type: 'error', text: message});
        } finally {
            setOrgConsoleDeleteProviderSaving(false);
        }
    }, [
        orgConsoleSelectedProvider,
        orgConsoleDeleteProviderTarget,
        tenantOrganization?.id,
        refreshOrgConsoleProviders,
    ]);

    if (!isAdminUser) {
        return (
            <DashboardLayout>
                <div className="text-center py-16">
                    <h1 className="text-2xl font-bold text-mld-text-primary mb-2">Access Denied</h1>
                    <p className="text-mld-text-secondary">Insufficient permission for this page.</p>
                </div>
            </DashboardLayout>
        );
    }

    const breadcrumbs = (() => {
        if (!isDashboardHost) {
            if (orgConsoleProviderViewActive && orgConsoleSelectedProvider) {
                return ['Org Console', tenantOrgTitle, orgConsoleSelectedProvider.name];
            }
            const currentLabel = orgConsoleTabLabels[orgConsoleTab] ?? 'Org Console';
            return ['Org Console', tenantOrgTitle, currentLabel];
        }
        if (activePage === 'global') return ['Admin', 'Global Dashboard'];
        if (activePage === 'lab-pricing') return ['Admin', 'Lab Pricing'];
        if (activePage === 'biomatch') return ['Admin', 'BioMatch'];
        if (activePage === 'global-products') return ['Admin', 'Global Products'];
        if (activePage === 'kit-costs') return ['Admin', 'Kit Costs'];
        if (activePage === 'loinc-codes') return ['Admin', 'Biomarkers'];
        if (activePage === 'biomarker-display-rules') return ['Admin', 'Biomarkers'];
        if (activePage === 'coupons') return ['Admin', 'Coupons'];
        if (activePage === 'orders') return ['Admin', 'Orders'];
        if (activePage === 'password-resets') return ['Admin', 'Password Resets'];
        if (activePage === 'fortis-integration') return ['Admin', 'Merchant Integration'];
        if (activePage !== 'org-detail') return ['Admin'];
        if (orgDetailTab === 'tests-catalog') {
            return ['Admin', 'Organizations', getOrganizationLabel(selectedOrg), 'Lab Tests Catalog'];
        }
        if (orgDetailTab === 'ref-ranges') {
            return ['Admin', 'Organizations', getOrganizationLabel(selectedOrg), 'Reference Ranges'];
        }
        if (orgDetailTab === 'api-keys') {
            return ['Admin', 'Organizations', getOrganizationLabel(selectedOrg), 'API Keys'];
        }
        return ['Admin', 'Organizations', getOrganizationLabel(selectedOrg)];
    })();

    const orgTitle = getOrganizationLabel(selectedOrg);
    const orgDomain =
        selectedOrg?.subdomain && hostInfo.baseDomain
            ? `${selectedOrg.subdomain}.${hostInfo.baseDomain}`
            : selectedOrg?.subdomain;
    const orgProviders =
        selectedOrg?.provider_count == null ? '—' : `${selectedOrg.provider_count.toLocaleString('en-US')}`;

    if (!isDashboardHost) {
        const features = tenantOrganization?.features as Record<string, unknown> | undefined;
        const isEmployeeHealthMode = !!features?.enable_employee_health_dashboard;
        const rawOhdOrgId = features?.ohd_organization_id;
        const ohdOrganizationId =
            typeof rawOhdOrgId === 'number'
                ? rawOhdOrgId
                : typeof rawOhdOrgId === 'string' && /^\d+$/.test(rawOhdOrgId)
                  ? Number(rawOhdOrgId)
                  : undefined;

        const orgConsoleDomain =
            tenantOrganization?.subdomain && hostInfo.baseDomain
                ? `${tenantOrganization.subdomain}.${hostInfo.baseDomain}`
                : hostInfo.hostname || 'Org Console';

        return (
            <AdminNotificationProvider>
                <div className="flex min-h-screen bg-mld-background">
                    {/* Whole aside is suppressed for OHD-mode org users —
                        with the primary nav + providers list hidden, the
                        bare logo column reads as broken. Org users get
                        wayfinding from the breadcrumbs at the top of main.
                        Super admins still see the aside for tenant-admin
                        tasks. */}
                    {(!isEmployeeHealthMode || isUserAdmin(user?.token)) && (
                        <aside className="sticky top-0 flex h-screen w-[280px] shrink-0 flex-col border-r border-mld-admin-border bg-mld-admin-surface">
                            <div className="flex h-14 items-center gap-3 border-b border-mld-admin-border px-4">
                                {orgConsoleLogoUrl ? (
                                    <img
                                        src={orgConsoleLogoUrl}
                                        alt={`${tenantOrgTitle} logo`}
                                        className="h-8 w-8 rounded-[8px] object-contain bg-white p-1"
                                    />
                                ) : (
                                    <div className="flex h-8 w-8 items-center justify-center rounded-[8px] bg-mld-primary text-[11px] font-semibold text-white">
                                        {getInitials(tenantOrgTitle)}
                                    </div>
                                )}
                                <div className="min-w-0">
                                    <div className="truncate text-sm font-semibold text-mld-admin-text">
                                        {tenantOrgTitle}
                                    </div>
                                    <div className="truncate text-[11px] text-mld-admin-muted">{orgConsoleDomain}</div>
                                </div>
                            </div>

                            <div className="border-b border-mld-admin-border px-3 py-3">
                                <button
                                    type="button"
                                    onClick={() => handleOrgConsolePrimaryNav('dashboard')}
                                    className={clsx(
                                        'mb-1 flex w-full items-center gap-2 rounded-[7px] px-3 py-2 text-xs font-semibold transition',
                                        orgConsoleTab === 'dashboard' && !orgConsoleProviderViewActive
                                            ? 'bg-mld-primary/10 text-mld-primary'
                                            : 'text-mld-admin-muted hover:bg-mld-admin-surface-hover hover:text-mld-admin-text'
                                    )}>
                                    <LayoutDashboard className="h-4 w-4" />
                                    Organization Dashboard
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleOrgConsolePrimaryNav('search')}
                                    className={clsx(
                                        'mb-1 flex w-full items-center gap-2 rounded-[7px] px-3 py-2 text-xs font-semibold transition',
                                        orgConsoleTab === 'search' && !orgConsoleProviderViewActive
                                            ? 'bg-mld-primary/10 text-mld-primary'
                                            : 'text-mld-admin-muted hover:bg-mld-admin-surface-hover hover:text-mld-admin-text'
                                    )}>
                                    <Users className="h-4 w-4" />
                                    Patient Search
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleOrgConsolePrimaryNav('password-resets')}
                                    className={clsx(
                                        'mb-1 flex w-full items-center gap-2 rounded-[7px] px-3 py-2 text-xs font-semibold transition',
                                        orgConsoleTab === 'password-resets' && !orgConsoleProviderViewActive
                                            ? 'bg-mld-primary/10 text-mld-primary'
                                            : 'text-mld-admin-muted hover:bg-mld-admin-surface-hover hover:text-mld-admin-text'
                                    )}>
                                    <KeyRound className="h-4 w-4" />
                                    Password Resets
                                </button>
                                {!!(tenantOrganization?.features as Record<string, unknown> | undefined)
                                    ?.enable_employee_health_dashboard && (
                                    <button
                                        type="button"
                                        onClick={() => setOhdMappingModalOpen(true)}
                                        className="mb-1 flex w-full items-center gap-2 rounded-[7px] px-3 py-2 text-xs font-semibold text-mld-admin-muted transition hover:bg-mld-admin-surface-hover hover:text-mld-admin-text">
                                        <Tag className="h-4 w-4" />
                                        OHD Test Mapping
                                    </button>
                                )}
                                {!!(tenantOrganization?.features as Record<string, unknown> | undefined)
                                    ?.enable_employee_health_dashboard &&
                                    isUserAdmin(user?.token) &&
                                    tenantOrganization?.id &&
                                    orgConsoleProviders.length === 1 && (
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                const orgId = tenantOrganization.id;
                                                const providerId = orgConsoleProviders[0].id;
                                                try {
                                                    const preview = await AdminService.backfillMisimportedOrgProvider(
                                                        orgId,
                                                        {
                                                            correctProvider: providerId,
                                                            apply: false,
                                                        }
                                                    );
                                                    if (preview.sourceOrgIds.length === 0) {
                                                        window.alert(
                                                            `No deprecated catch-all org (${preview.sourceSubdomains.join(', ')}) exists in this environment. Nothing to remap.`
                                                        );
                                                        return;
                                                    }
                                                    if (preview.localUsersMatched === 0) {
                                                        window.alert(
                                                            "No local users matched this org's OHD roster. Nothing to remap."
                                                        );
                                                        return;
                                                    }
                                                    const a = preview.affectedRows;
                                                    if (
                                                        !window.confirm(
                                                            `Re-tag mis-imported rows from the deprecated catch-all [${preview.sourceSubdomains.join(', ')}] to THIS org (id ${orgId}, provider ${providerId})?\n\n` +
                                                                `orders: ${a.orders}\nresults: ${a.results}\nOHD result sets: ${a.ohd_result_sets}\nprovider links: ${a.user_providers}\n\n` +
                                                                `This UPDATEs those rows in one transaction.`
                                                        )
                                                    )
                                                        return;
                                                    const result = await AdminService.backfillMisimportedOrgProvider(
                                                        orgId,
                                                        {
                                                            correctProvider: providerId,
                                                            apply: true,
                                                        }
                                                    );
                                                    const u = result.updates;
                                                    window.alert(
                                                        u
                                                            ? `Done.\norders: ${u.orders}\nresults: ${u.results}\nOHD result sets: ${u.ohdResultSets}\nprovider links ensured: ${u.userProvidersEnsured}, deleted: ${u.userProvidersDeleted}`
                                                            : 'No changes applied.'
                                                    );
                                                } catch (err) {
                                                    window.alert(
                                                        'Fix failed: ' +
                                                            (err instanceof Error ? err.message : String(err))
                                                    );
                                                }
                                            }}
                                            className="mb-1 flex w-full items-center gap-2 rounded-[7px] px-3 py-2 text-xs font-semibold text-mld-admin-muted transition hover:bg-mld-admin-surface-hover hover:text-mld-admin-text">
                                            <Wrench className="h-4 w-4" />
                                            Fix Mis-Imported Orders
                                        </button>
                                    )}
                            </div>

                            <div className="flex items-center justify-between px-4 pt-3">
                                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-mld-admin-muted">
                                    <span>Providers ({orgConsoleVisibleProviders.length.toLocaleString('en-US')})</span>
                                    {orgConsoleInactiveCount > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setOrgConsoleShowInactive((prev) => !prev)}
                                            className="normal-case tracking-normal text-mld-primary underline hover:text-mld-primary">
                                            {orgConsoleShowInactive
                                                ? 'Hide inactive'
                                                : `Show all (${orgConsoleProviderCount})`}
                                        </button>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setOrgConsoleAddProviderName('');
                                        setOrgConsoleAddProviderNpi('');
                                        setOrgConsoleAddProviderMessage({type: '', text: ''});
                                        setOrgConsoleAddProviderOpen(true);
                                    }}
                                    className="inline-flex items-center gap-1 rounded-md border border-mld-admin-border px-2 py-1 text-[10px] font-semibold text-mld-admin-muted hover:text-mld-admin-text">
                                    <Plus className="h-3 w-3" />
                                    Add
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto px-2 pb-3">
                                {orgConsoleProvidersLoading ? (
                                    <div className="px-3 py-4 text-xs text-mld-admin-muted">Loading providers...</div>
                                ) : orgConsoleVisibleProviders.length === 0 ? (
                                    <div className="px-3 py-4 text-xs text-mld-admin-muted">No providers found.</div>
                                ) : (
                                    orgConsoleVisibleProviders.map((provider) => {
                                        const isSelected =
                                            orgConsoleProviderViewActive &&
                                            provider.id === orgConsoleSelectedProviderId;
                                        const isInactive = provider.is_active === false;
                                        return (
                                            <button
                                                key={provider.id}
                                                type="button"
                                                onClick={() => handleOrgConsoleProviderSelect(provider.id)}
                                                className={clsx(
                                                    'mb-1 flex w-full items-center gap-3 rounded-[7px] px-3 py-2 text-left transition',
                                                    isSelected
                                                        ? 'border-l-2 border-mld-primary bg-mld-primary/10'
                                                        : 'border-l-2 border-transparent hover:bg-mld-admin-surface-hover',
                                                    isInactive && !isSelected && 'opacity-50'
                                                )}>
                                                <div
                                                    className={clsx(
                                                        'flex h-8 w-8 items-center justify-center rounded-[7px] text-[11px] font-semibold',
                                                        isSelected
                                                            ? 'bg-mld-primary text-white'
                                                            : 'bg-mld-admin-surface-hover text-mld-admin-muted'
                                                    )}>
                                                    {getInitials(provider.name)}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div
                                                        className={clsx(
                                                            'truncate text-xs font-semibold',
                                                            isSelected ? 'text-mld-primary' : 'text-mld-admin-text'
                                                        )}>
                                                        {provider.name}
                                                    </div>
                                                    <div className="truncate text-[11px] text-mld-admin-muted">
                                                        {provider.is_active ? 'ELIS Active' : 'ELIS Inactive'}
                                                    </div>
                                                </div>
                                                <span
                                                    className={clsx(
                                                        'h-2 w-2 rounded-full',
                                                        provider.is_active
                                                            ? 'bg-mld-status-normal'
                                                            : 'bg-mld-muted-text'
                                                    )}
                                                />
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </aside>
                    )}

                    <main className="flex min-h-0 min-w-0 flex-1 flex-col">
                        <div className="flex h-14 items-center gap-3 border-b border-mld-panel-border bg-mld-menu-background px-6">
                            {/* When the aside is hidden (OHD-mode org user),
                                surface the org logo + name here so the page
                                still has tenant branding/wayfinding. */}
                            {isEmployeeHealthMode && !isUserAdmin(user?.token) && (
                                <div className="flex items-center gap-3 pr-3">
                                    {orgConsoleLogoUrl ? (
                                        <img
                                            src={orgConsoleLogoUrl}
                                            alt={`${tenantOrgTitle} logo`}
                                            className="h-8 w-8 rounded-[8px] bg-white object-contain p-1"
                                        />
                                    ) : (
                                        <div className="flex h-8 w-8 items-center justify-center rounded-[8px] bg-mld-primary text-[11px] font-semibold text-white">
                                            {getInitials(tenantOrgTitle)}
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <div className="truncate text-sm font-semibold text-mld-menu-text">
                                            {tenantOrgTitle}
                                        </div>
                                        <div className="truncate text-[11px] text-mld-muted-text">
                                            {orgConsoleDomain}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="flex items-center gap-2 text-sm text-mld-muted-text">
                                {breadcrumbs.map((crumb, index) => (
                                    <React.Fragment key={`${crumb}-${index}`}>
                                        <span
                                            className={clsx(
                                                index === breadcrumbs.length - 1 ? 'text-mld-text-secondary' : ''
                                            )}>
                                            {crumb}
                                        </span>
                                        {index < breadcrumbs.length - 1 ? (
                                            <span className="text-mld-muted-text">›</span>
                                        ) : null}
                                    </React.Fragment>
                                ))}
                            </div>
                            <div className="ml-auto flex items-center gap-3">
                                {adminOrigin && isUserAdmin(user?.token) ? (
                                    <Button
                                        variant="secondary"
                                        size="md"
                                        onClick={handleBackToAdmin}
                                        iconLeft={<ArrowLeft className="h-3.5 w-3.5" />}>
                                        Back to Admin Dashboard
                                    </Button>
                                ) : null}
                                <div className="relative" ref={userMenuRef}>
                                    <button
                                        type="button"
                                        onClick={() => setUserMenuOpen((prev) => !prev)}
                                        className="flex items-center gap-2 rounded-full px-2.5 py-1.5 text-sm text-mld-text-secondary transition hover:text-mld-menu-text">
                                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-mld-menu-text text-[11px] font-semibold text-white">
                                            {getInitials(userName)}
                                        </div>
                                        <div className="hidden sm:flex flex-col text-left leading-tight">
                                            <span className="text-sm font-semibold text-mld-menu-text">{userName}</span>
                                            <span className="text-[11px] text-mld-muted-text">
                                                {isUserAdmin(user?.token) ? 'Super Admin' : 'Org Admin'}
                                            </span>
                                        </div>
                                        <ChevronDown
                                            className={clsx(
                                                'hidden sm:block h-4 w-4 text-mld-muted-text transition-transform',
                                                userMenuOpen ? 'rotate-180' : ''
                                            )}
                                        />
                                    </button>
                                    {userMenuOpen ? (
                                        <div className="absolute right-0 top-full mt-2 w-40 rounded-lg border border-mld-panel-border bg-white py-1 shadow-lg z-20">
                                            <button
                                                type="button"
                                                onClick={handleLogout}
                                                className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold text-mld-danger hover:bg-mld-background">
                                                <LogOut className="h-4 w-4" />
                                                Logout
                                            </button>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>

                        {!isEmployeeHealthMode && (
                            <div className="px-7 pt-6">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-center gap-4">
                                        {orgConsoleLogoUrl ? (
                                            <div className="flex h-12 w-12 items-center justify-center rounded-[11px] bg-white">
                                                <img
                                                    src={orgConsoleLogoUrl}
                                                    alt={`${tenantOrgTitle} logo`}
                                                    className="h-10 w-10 rounded-[10px] object-contain"
                                                />
                                            </div>
                                        ) : (
                                            <div className="flex h-12 w-12 items-center justify-center rounded-[11px] bg-gradient-to-br from-mld-primary to-mld-primary text-sm font-semibold text-white">
                                                {getInitials(tenantOrgTitle)}
                                            </div>
                                        )}
                                        <div>
                                            <div className="text-lg font-semibold text-mld-menu-text">
                                                {tenantOrgTitle}
                                            </div>
                                            <div className="text-xs text-mld-muted-text">
                                                {orgConsoleDomain} ·{' '}
                                                {orgConsoleActiveProviderCount.toLocaleString('en-US')}{' '}
                                                {orgConsoleActiveProviderCount === 1 ? 'provider' : 'providers'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {!orgConsoleProviderViewActive && !isEmployeeHealthMode ? (
                            <div className="px-7">
                                <div className="flex flex-wrap gap-4 border-b border-mld-panel-border">
                                    {orgConsoleSecondaryTabs.map((tab) => (
                                        <button
                                            key={tab.id}
                                            type="button"
                                            onClick={() => setOrgConsoleTab(tab.id)}
                                            className={clsx(
                                                'border-b-2 px-3 py-3 text-sm font-semibold transition',
                                                orgConsoleTab === tab.id
                                                    ? 'border-mld-primary text-mld-primary'
                                                    : 'border-transparent text-mld-muted-text hover:text-mld-text-secondary'
                                            )}>
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : null}

                        <div className="flex-1 overflow-y-auto px-7 py-6">
                            <AdminNotificationBanner className="mb-6" />
                            {orgConsoleProviderViewActive ? (
                                orgConsoleSelectedProvider ? (
                                    <div className="rounded-xl border border-mld-panel-border bg-white px-6 py-5">
                                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                            <div>
                                                <div className="flex flex-wrap items-center gap-2">
                                                    {orgConsoleProviderNameEditing ? (
                                                        <input
                                                            value={orgConsoleProviderNameDraft}
                                                            onChange={(event) =>
                                                                setOrgConsoleProviderNameDraft(event.target.value)
                                                            }
                                                            className="min-w-[220px] rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-mld-menu-text focus:border-mld-primary focus:outline-none"
                                                        />
                                                    ) : (
                                                        <div className="text-lg font-semibold text-mld-menu-text">
                                                            {orgConsoleSelectedProvider.name}
                                                        </div>
                                                    )}
                                                    <span
                                                        className={clsx(
                                                            'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold',
                                                            orgConsoleSelectedProvider.is_active
                                                                ? 'bg-mld-success/5 text-mld-success'
                                                                : 'bg-mld-menu-background text-mld-muted-text'
                                                        )}>
                                                        {orgConsoleSelectedProvider.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {orgConsoleProviderNameEditing ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={handleProviderNameCancel}
                                                            disabled={orgConsoleProviderNameSaving}
                                                            className="inline-flex items-center gap-1 rounded-md border border-mld-panel-border px-3 py-1.5 text-xs font-semibold text-mld-muted-text hover:text-mld-menu-text">
                                                            <X className="h-3 w-3" />
                                                            Cancel
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={handleProviderNameSave}
                                                            disabled={orgConsoleProviderNameSaving}
                                                            className="inline-flex items-center gap-1 rounded-md bg-mld-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-mld-primary/90 disabled:opacity-60">
                                                            {orgConsoleProviderNameSaving ? (
                                                                <>
                                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                                    Saving...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Save className="h-3 w-3" />
                                                                    Save Name
                                                                </>
                                                            )}
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={handleProviderNameEdit}
                                                        className="inline-flex items-center gap-1 rounded-md border border-mld-panel-border px-3 py-1.5 text-xs font-semibold text-mld-muted-text hover:text-mld-menu-text">
                                                        <Pencil className="h-3 w-3" />
                                                        Edit Name
                                                    </button>
                                                )}
                                                {orgConsoleProviders.length >= 2 ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setOrgConsoleDeleteProviderOpen(true);
                                                            setOrgConsoleDeleteProviderTarget(null);
                                                            setOrgConsoleDeleteProviderMessage({type: '', text: ''});
                                                        }}
                                                        className="inline-flex items-center gap-1 rounded-md border border-mld-danger/30 px-3 py-1.5 text-xs font-semibold text-mld-danger hover:bg-mld-danger/5">
                                                        <Trash2 className="h-3 w-3" />
                                                        Remove Provider
                                                    </button>
                                                ) : null}
                                            </div>
                                        </div>
                                        {orgConsoleProviderNameMessage.text ? (
                                            <div
                                                className={clsx(
                                                    'mt-2 text-xs',
                                                    orgConsoleProviderNameMessage.type === 'error'
                                                        ? 'text-mld-danger'
                                                        : 'text-mld-success'
                                                )}>
                                                {orgConsoleProviderNameMessage.text}
                                            </div>
                                        ) : null}

                                        {orgConsoleDeleteProviderOpen && orgConsoleSelectedProvider ? (
                                            <div className="mt-4 rounded-md border border-mld-danger/20 bg-mld-danger/5 px-4 py-4">
                                                <p className="mb-1 text-sm font-semibold text-mld-danger">
                                                    Delete {orgConsoleSelectedProvider.name}
                                                </p>
                                                <p className="mb-3 text-xs text-mld-danger">
                                                    All patients, orders, and data will be reassigned to the selected
                                                    provider. This cannot be undone.
                                                </p>
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <label className="text-xs font-semibold text-mld-text-secondary whitespace-nowrap">
                                                        Transfer data to:
                                                    </label>
                                                    <select
                                                        value={orgConsoleDeleteProviderTarget ?? ''}
                                                        onChange={(event) =>
                                                            setOrgConsoleDeleteProviderTarget(
                                                                event.target.value ? Number(event.target.value) : null
                                                            )
                                                        }
                                                        className={`min-w-[200px] rounded-md border border-mld-panel-border px-3 py-2 pr-8 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary ${orgConsoleDeleteProviderTarget == null ? 'text-mld-muted-text' : 'text-mld-text-primary'}`}>
                                                        <option value="" disabled>
                                                            Select provider
                                                        </option>
                                                        {orgConsoleProviders
                                                            .filter(
                                                                (p) =>
                                                                    p.id !== orgConsoleSelectedProvider.id &&
                                                                    p.is_active !== false
                                                            )
                                                            .map((p) => (
                                                                <option key={p.id} value={p.id}>
                                                                    {p.name}
                                                                </option>
                                                            ))}
                                                    </select>
                                                    <button
                                                        type="button"
                                                        onClick={handleDeleteProviderSubmit}
                                                        disabled={
                                                            !orgConsoleDeleteProviderTarget ||
                                                            orgConsoleDeleteProviderSaving
                                                        }
                                                        className={`inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm disabled:opacity-50 ${orgConsoleDeleteProviderTarget ? 'border-mld-danger bg-mld-danger text-white hover:bg-mld-danger/80' : 'border-mld-danger/30 bg-transparent text-mld-danger/60'}`}>
                                                        {orgConsoleDeleteProviderSaving ? (
                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                        ) : null}
                                                        Delete {orgConsoleSelectedProvider.name}
                                                    </button>
                                                    <Button
                                                        variant="secondary"
                                                        size="md"
                                                        onClick={() => {
                                                            setOrgConsoleDeleteProviderOpen(false);
                                                            setOrgConsoleDeleteProviderTarget(null);
                                                            setOrgConsoleDeleteProviderMessage({type: '', text: ''});
                                                        }}>
                                                        Cancel
                                                    </Button>
                                                </div>
                                                {orgConsoleDeleteProviderMessage.text ? (
                                                    <div
                                                        className={clsx(
                                                            'mt-3 text-sm',
                                                            orgConsoleDeleteProviderMessage.type === 'error'
                                                                ? 'text-mld-danger'
                                                                : 'text-mld-success'
                                                        )}>
                                                        {orgConsoleDeleteProviderMessage.text}
                                                    </div>
                                                ) : null}
                                            </div>
                                        ) : null}

                                        <div className="mt-4">
                                            <div className="text-sm font-semibold text-mld-text-secondary">Modules</div>
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                {ProviderConsoleModuleRegistry.map((module) => {
                                                    const enabled =
                                                        orgConsoleSelectedProviderModuleMap.get(module.id) ?? false;
                                                    const orgEnabled =
                                                        orgConsoleOrganizationModuleMap.get(module.id) ?? false;
                                                    const locked = !orgEnabled && !enabled;
                                                    return (
                                                        <button
                                                            key={module.id}
                                                            type="button"
                                                            onClick={() => handleOrgConsoleModuleToggle(module.id)}
                                                            disabled={locked || orgConsoleProviderModulesSaving}
                                                            title={
                                                                locked
                                                                    ? 'Enable this module at the organization level first.'
                                                                    : 'Toggle provider module'
                                                            }
                                                            className={clsx(
                                                                'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold transition',
                                                                enabled
                                                                    ? 'bg-mld-success/5 text-mld-success hover:bg-mld-success/10'
                                                                    : 'bg-mld-menu-background text-mld-muted-text hover:bg-mld-panel-border',
                                                                locked && 'cursor-not-allowed opacity-60'
                                                            )}>
                                                            {module.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            {orgConsoleProviderModulesMessage.text ? (
                                                <div
                                                    className={clsx(
                                                        'mt-2 text-xs',
                                                        orgConsoleProviderModulesMessage.type === 'error'
                                                            ? 'text-mld-danger'
                                                            : 'text-mld-success'
                                                    )}>
                                                    {orgConsoleProviderModulesMessage.text}
                                                </div>
                                            ) : null}
                                        </div>

                                        <div className="mt-6">
                                            <div className="flex flex-wrap items-center justify-between gap-3">
                                                <div className="text-sm font-semibold text-mld-text-secondary">
                                                    Provider Users
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setOrgConsoleProviderUserAddOpen((prev) => !prev)}
                                                    className="inline-flex items-center gap-2 rounded-md border border-mld-primary px-3 py-1.5 text-xs font-semibold text-mld-primary hover:bg-mld-primary/10">
                                                    <Plus className="h-3 w-3" />
                                                    {orgConsoleProviderUserAddOpen ? 'Hide' : 'Add Provider User'}
                                                </button>
                                            </div>
                                            {orgConsoleProviderUserMessage.text && !orgConsoleProviderUserAddOpen ? (
                                                <div
                                                    className={clsx(
                                                        'mt-2 text-xs',
                                                        orgConsoleProviderUserMessage.type === 'error'
                                                            ? 'text-mld-danger'
                                                            : 'text-mld-success'
                                                    )}>
                                                    {orgConsoleProviderUserMessage.text}
                                                </div>
                                            ) : null}

                                            {orgConsoleProviderUserAddOpen ? (
                                                <div className="mt-3 rounded-lg border border-mld-panel-border bg-mld-menu-background px-4 py-3">
                                                    <div className="text-xs font-semibold text-mld-muted-text">
                                                        New provider user
                                                    </div>
                                                    <div className="mt-3 flex flex-wrap items-center gap-3">
                                                        <input
                                                            value={orgConsoleProviderUserForm.first_name}
                                                            onChange={(event) =>
                                                                handleProviderUserFieldChange(
                                                                    'first_name',
                                                                    event.target.value
                                                                )
                                                            }
                                                            placeholder="First name"
                                                            className="min-w-[160px] flex-1 rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-mld-primary focus:outline-none"
                                                        />
                                                        <input
                                                            value={orgConsoleProviderUserForm.last_name}
                                                            onChange={(event) =>
                                                                handleProviderUserFieldChange(
                                                                    'last_name',
                                                                    event.target.value
                                                                )
                                                            }
                                                            placeholder="Last name"
                                                            className="min-w-[160px] flex-1 rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-mld-primary focus:outline-none"
                                                        />
                                                        <input
                                                            value={orgConsoleProviderUserForm.email}
                                                            onChange={(event) =>
                                                                handleProviderUserFieldChange(
                                                                    'email',
                                                                    event.target.value
                                                                )
                                                            }
                                                            placeholder="Email"
                                                            className="min-w-[200px] flex-[1.5] rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-mld-primary focus:outline-none"
                                                        />
                                                        <input
                                                            value={orgConsoleProviderUserForm.phone}
                                                            onChange={(event) =>
                                                                handleProviderUserFieldChange(
                                                                    'phone',
                                                                    event.target.value
                                                                )
                                                            }
                                                            placeholder="Phone"
                                                            className="min-w-[160px] flex-1 rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-mld-primary focus:outline-none"
                                                        />
                                                        <select
                                                            value={orgConsoleProviderUserForm.role_id ?? ''}
                                                            onChange={(event) =>
                                                                handleProviderUserFieldChange(
                                                                    'role_id',
                                                                    event.target.value
                                                                        ? Number(event.target.value)
                                                                        : null
                                                                )
                                                            }
                                                            disabled={orgConsoleSelectedProviderRoles.length === 0}
                                                            className="min-w-[160px] flex-1 rounded-md border border-mld-panel-border px-2 py-2 text-sm text-mld-text-secondary">
                                                            <option value="">No role</option>
                                                            {orgConsoleSelectedProviderRoles.map((role) => (
                                                                <option key={role.id} value={role.id}>
                                                                    {role.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    {orgConsoleSelectedProviderRoles.length === 0 ? (
                                                        <div className="mt-2 text-[11px] text-mld-muted-text">
                                                            No provider roles found.
                                                        </div>
                                                    ) : null}
                                                    <div className="mt-3 flex items-center gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={handleCreateProviderUser}
                                                            disabled={orgConsoleProviderUserSaving}
                                                            className="inline-flex items-center gap-2 rounded-md bg-mld-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-mld-primary/90">
                                                            <Save className="h-3 w-3" />
                                                            Create User
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setOrgConsoleProviderUserAddOpen(false)}
                                                            className="inline-flex items-center gap-2 rounded-md border border-mld-panel-border px-3 py-1.5 text-xs font-semibold text-mld-muted-text hover:text-mld-menu-text">
                                                            <X className="h-3 w-3" />
                                                            Cancel
                                                        </button>
                                                    </div>
                                                    {orgConsoleProviderUserMessage.text ? (
                                                        <div
                                                            className={clsx(
                                                                'mt-2 text-xs',
                                                                orgConsoleProviderUserMessage.type === 'error'
                                                                    ? 'text-mld-danger'
                                                                    : 'text-mld-success'
                                                            )}>
                                                            {orgConsoleProviderUserMessage.text}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            ) : null}

                                            {orgConsoleSelectedProviderUsers.length === 0 ? (
                                                <div className="mt-2 text-sm text-mld-muted-text">
                                                    No provider users found.
                                                </div>
                                            ) : (
                                                <div className="mt-3 overflow-x-auto rounded-lg border border-mld-panel-border">
                                                    <table className="min-w-full divide-y divide-mld-panel-border text-sm">
                                                        <thead className="bg-mld-background text-xs text-mld-muted-text">
                                                            <tr>
                                                                <th className="px-4 py-2 text-left font-semibold">
                                                                    Name
                                                                </th>
                                                                <th className="px-4 py-2 text-left font-semibold">
                                                                    Email
                                                                </th>
                                                                <th className="px-4 py-2 text-left font-semibold">
                                                                    Roles
                                                                </th>
                                                                <th className="px-4 py-2 text-left font-semibold">
                                                                    Actions
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-mld-panel-border bg-white">
                                                            {orgConsoleSelectedProviderUsers.map((user) => {
                                                                const roles =
                                                                    orgConsoleSelectedProviderRolesByUser[user.id] ??
                                                                    [];
                                                                const displayName = [user.first_name, user.last_name]
                                                                    .filter(Boolean)
                                                                    .join(' ')
                                                                    .trim();
                                                                const isEditing =
                                                                    orgConsoleProviderUserEdit?.id === user.id;
                                                                return (
                                                                    <React.Fragment key={user.id}>
                                                                        <tr>
                                                                            <td className="px-4 py-3 text-mld-text-secondary">
                                                                                {displayName || '—'}
                                                                            </td>
                                                                            <td className="px-4 py-3 text-mld-text-secondary">
                                                                                {user.email}
                                                                            </td>
                                                                            <td className="px-4 py-3 text-mld-text-secondary">
                                                                                {roles.length > 0
                                                                                    ? roles.join(', ')
                                                                                    : '—'}
                                                                            </td>
                                                                            <td className="px-4 py-3 text-mld-text-secondary">
                                                                                <div className="flex items-center gap-2">
                                                                                    {orgConsoleSelectedProviderId ? (
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() =>
                                                                                                handleViewProviderConsole(
                                                                                                    orgConsoleSelectedProviderId,
                                                                                                    {
                                                                                                        orgId:
                                                                                                            tenantOrganization?.id ??
                                                                                                            null,
                                                                                                        subdomain:
                                                                                                            tenantOrganization?.subdomain,
                                                                                                        returnState:
                                                                                                            tenantOrganization?.id
                                                                                                                ? {
                                                                                                                      orgId: tenantOrganization.id,
                                                                                                                      orgConsole:
                                                                                                                          '1',
                                                                                                                  }
                                                                                                                : undefined,
                                                                                                    }
                                                                                                )
                                                                                            }
                                                                                            disabled={
                                                                                                orgConsoleProviderSwitching ===
                                                                                                orgConsoleSelectedProviderId
                                                                                            }
                                                                                            className="inline-flex items-center gap-1 rounded-md border border-mld-primary px-2 py-1 text-[11px] font-semibold text-mld-primary hover:bg-mld-primary/10 disabled:opacity-60">
                                                                                            <ExternalLink className="h-3 w-3" />
                                                                                            {orgConsoleProviderSwitching ===
                                                                                            orgConsoleSelectedProviderId
                                                                                                ? 'Switching…'
                                                                                                : 'View Console'}
                                                                                        </button>
                                                                                    ) : null}
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() =>
                                                                                            isEditing
                                                                                                ? setOrgConsoleProviderUserEdit(
                                                                                                      null
                                                                                                  )
                                                                                                : handleStartProviderUserEdit(
                                                                                                      user
                                                                                                  )
                                                                                        }
                                                                                        className="inline-flex items-center gap-1 rounded-md border border-mld-panel-border px-2 py-1 text-[11px] font-semibold text-mld-muted-text hover:text-mld-menu-text">
                                                                                        <Pencil className="h-3 w-3" />
                                                                                        {isEditing ? 'Close' : 'Edit'}
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() =>
                                                                                            handleDeleteProviderUser(
                                                                                                user.id
                                                                                            )
                                                                                        }
                                                                                        disabled={
                                                                                            orgConsoleProviderUserDeletingId ===
                                                                                                user.id ||
                                                                                            orgConsoleProviderUserSaving
                                                                                        }
                                                                                        className={clsx(
                                                                                            'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-semibold',
                                                                                            orgConsoleProviderUserDeletingId ===
                                                                                                user.id
                                                                                                ? 'border-mld-panel-border text-mld-muted-text'
                                                                                                : 'border-mld-danger/20 text-mld-danger hover:border-mld-danger/30'
                                                                                        )}>
                                                                                        <Trash2 className="h-3 w-3" />
                                                                                        Remove
                                                                                    </button>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                        {isEditing ? (
                                                                            <tr className="bg-mld-menu-background">
                                                                                <td colSpan={4} className="px-4 py-4">
                                                                                    <div className="flex flex-wrap items-center gap-3">
                                                                                        <input
                                                                                            value={
                                                                                                orgConsoleProviderUserEdit
                                                                                                    .form.first_name
                                                                                            }
                                                                                            onChange={(event) =>
                                                                                                handleProviderUserEditFieldChange(
                                                                                                    'first_name',
                                                                                                    event.target.value
                                                                                                )
                                                                                            }
                                                                                            placeholder="First name"
                                                                                            className="min-w-[160px] flex-1 rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-mld-primary focus:outline-none"
                                                                                        />
                                                                                        <input
                                                                                            value={
                                                                                                orgConsoleProviderUserEdit
                                                                                                    .form.last_name
                                                                                            }
                                                                                            onChange={(event) =>
                                                                                                handleProviderUserEditFieldChange(
                                                                                                    'last_name',
                                                                                                    event.target.value
                                                                                                )
                                                                                            }
                                                                                            placeholder="Last name"
                                                                                            className="min-w-[160px] flex-1 rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-mld-primary focus:outline-none"
                                                                                        />
                                                                                        <input
                                                                                            value={
                                                                                                orgConsoleProviderUserEdit
                                                                                                    .form.email
                                                                                            }
                                                                                            onChange={(event) =>
                                                                                                handleProviderUserEditFieldChange(
                                                                                                    'email',
                                                                                                    event.target.value
                                                                                                )
                                                                                            }
                                                                                            placeholder="Email"
                                                                                            className="min-w-[200px] flex-[1.5] rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-mld-primary focus:outline-none"
                                                                                        />
                                                                                        <input
                                                                                            value={
                                                                                                orgConsoleProviderUserEdit
                                                                                                    .form.phone
                                                                                            }
                                                                                            onChange={(event) =>
                                                                                                handleProviderUserEditFieldChange(
                                                                                                    'phone',
                                                                                                    event.target.value
                                                                                                )
                                                                                            }
                                                                                            placeholder="Phone"
                                                                                            className="min-w-[160px] flex-1 rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-mld-primary focus:outline-none"
                                                                                        />
                                                                                        <select
                                                                                            value={
                                                                                                orgConsoleProviderUserEdit
                                                                                                    .form.role_id ?? ''
                                                                                            }
                                                                                            onChange={(event) =>
                                                                                                handleProviderUserEditFieldChange(
                                                                                                    'role_id',
                                                                                                    event.target.value
                                                                                                        ? Number(
                                                                                                              event
                                                                                                                  .target
                                                                                                                  .value
                                                                                                          )
                                                                                                        : null
                                                                                                )
                                                                                            }
                                                                                            disabled={
                                                                                                orgConsoleSelectedProviderRoles.length ===
                                                                                                0
                                                                                            }
                                                                                            className="min-w-[160px] flex-1 rounded-md border border-mld-panel-border px-2 py-2 text-sm text-mld-text-secondary">
                                                                                            <option value="">
                                                                                                No role
                                                                                            </option>
                                                                                            {orgConsoleSelectedProviderRoles.map(
                                                                                                (role) => (
                                                                                                    <option
                                                                                                        key={role.id}
                                                                                                        value={role.id}>
                                                                                                        {role.name}
                                                                                                    </option>
                                                                                                )
                                                                                            )}
                                                                                        </select>
                                                                                    </div>
                                                                                    <div className="mt-3 flex items-center gap-2">
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={
                                                                                                handleUpdateProviderUser
                                                                                            }
                                                                                            disabled={
                                                                                                orgConsoleProviderUserSaving
                                                                                            }
                                                                                            className="inline-flex items-center gap-2 rounded-md bg-mld-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-mld-primary/90">
                                                                                            <Save className="h-3 w-3" />
                                                                                            Save
                                                                                        </button>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() =>
                                                                                                setOrgConsoleProviderUserEdit(
                                                                                                    null
                                                                                                )
                                                                                            }
                                                                                            className="inline-flex items-center gap-2 rounded-md border border-mld-panel-border px-3 py-1.5 text-xs font-semibold text-mld-muted-text hover:text-mld-menu-text">
                                                                                            <X className="h-3 w-3" />
                                                                                            Cancel
                                                                                        </button>
                                                                                    </div>
                                                                                    {orgConsoleProviderUserEditMessage.text ? (
                                                                                        <div
                                                                                            className={clsx(
                                                                                                'mt-2 text-xs',
                                                                                                orgConsoleProviderUserEditMessage.type ===
                                                                                                    'error'
                                                                                                    ? 'text-mld-danger'
                                                                                                    : 'text-mld-success'
                                                                                            )}>
                                                                                            {
                                                                                                orgConsoleProviderUserEditMessage.text
                                                                                            }
                                                                                        </div>
                                                                                    ) : null}
                                                                                </td>
                                                                            </tr>
                                                                        ) : null}
                                                                    </React.Fragment>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="rounded-xl border border-mld-panel-border bg-white px-6 py-5 text-sm text-mld-muted-text">
                                        Select a provider on the left to view details.
                                    </div>
                                )
                            ) : null}
                            {isEmployeeHealthMode && !orgConsoleProviderViewActive && orgConsoleTab === 'dashboard' ? (
                                <EmployeeHealthDashboard
                                    ohdOrganizationId={ohdOrganizationId}
                                    mldOrganizationId={tenantOrganization?.id}
                                />
                            ) : null}
                            {!isEmployeeHealthMode && !orgConsoleProviderViewActive && orgConsoleTab === 'dashboard' ? (
                                <div className="space-y-6">
                                    <div className="rounded-2xl border border-mld-panel-border bg-mld-menu-background px-6 py-5">
                                        <div className="text-lg font-semibold text-mld-menu-text">{tenantOrgTitle}</div>
                                        <div className="text-sm text-mld-muted-text">{orgConsoleDomain}</div>
                                        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                            <div className="rounded-lg border border-mld-panel-border bg-mld-background px-4 py-3">
                                                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-mld-muted-text">
                                                    Providers
                                                </div>
                                                <div className="mt-2 text-2xl font-semibold text-mld-menu-text">
                                                    {orgConsoleActiveProviderCount.toLocaleString('en-US')}
                                                </div>
                                            </div>
                                            {tenantOrganization?.support_email ? (
                                                <div className="rounded-lg border border-mld-panel-border bg-mld-background px-4 py-3">
                                                    <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-mld-muted-text">
                                                        Support Email
                                                    </div>
                                                    <div className="mt-2 text-sm font-medium text-mld-menu-text">
                                                        {tenantOrganization.support_email}
                                                    </div>
                                                </div>
                                            ) : null}
                                            {tenantOrganization?.support_phone ? (
                                                <div className="rounded-lg border border-mld-panel-border bg-mld-background px-4 py-3">
                                                    <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-mld-muted-text">
                                                        Support Phone
                                                    </div>
                                                    <div className="mt-2 text-sm font-medium text-mld-menu-text">
                                                        {formatPhoneInput(tenantOrganization.support_phone)}
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                            {/* Patient Search + Password Resets render in both MDX and OHD org-console
                                modes (the OHD dashboard only owns the 'dashboard' tab). In OHD mode
                                they are gated to super-admins — mirroring the aside-menu guard above —
                                so an OHD organization's own users can never reach patient search/edit,
                                even via a crafted ?ps= URL. (Defense-in-depth: the search/edit
                                endpoints are already super-admin-only server-side.) */}
                            {!orgConsoleProviderViewActive &&
                            orgConsoleTab === 'search' &&
                            (!isEmployeeHealthMode || isUserAdmin(user?.token)) ? (
                                <AdminSearch
                                    setPinnedMetrics={setPinnedMetrics}
                                    initialUserSearch={patientSearchSeed}
                                />
                            ) : null}
                            {!orgConsoleProviderViewActive &&
                            orgConsoleTab === 'password-resets' &&
                            (!isEmployeeHealthMode || isUserAdmin(user?.token)) ? (
                                <PasswordReset />
                            ) : null}
                            {!isEmployeeHealthMode &&
                            !orgConsoleProviderViewActive &&
                            orgConsoleTab === 'rejected-emails' ? (
                                <RejectedEmails />
                            ) : null}
                            {!isEmployeeHealthMode && !orgConsoleProviderViewActive && orgConsoleTab === 'catalog' ? (
                                <OrganizationCatalog />
                            ) : null}
                            {!isEmployeeHealthMode &&
                            !orgConsoleProviderViewActive &&
                            orgConsoleTab === 'recommendations' ? (
                                <BioMatchManager orgId={tenantOrganization?.id} />
                            ) : null}
                            {!isEmployeeHealthMode &&
                            !orgConsoleProviderViewActive &&
                            orgConsoleTab === 'storefront-products' &&
                            tenantOrganization?.id ? (
                                <StorefrontProductBuilder orgId={tenantOrganization.id} />
                            ) : null}
                            {!isEmployeeHealthMode &&
                            !orgConsoleProviderViewActive &&
                            orgConsoleTab === 'api-keys' &&
                            canManageProviderApiKeys &&
                            tenantOrganization?.id ? (
                                <ProviderApiKeys orgId={tenantOrganization.id} />
                            ) : null}
                        </div>
                    </main>
                </div>

                <Dialog
                    open={orgConsoleAddProviderOpen}
                    onClose={() => {
                        setOrgConsoleAddProviderOpen(false);
                        setOrgConsoleAddProviderName('');
                        setOrgConsoleAddProviderNpi('');
                        setOrgConsoleAddProviderMessage({type: '', text: ''});
                    }}
                    className="relative z-50">
                    <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center px-4 py-8">
                            <Dialog.Panel className="w-full max-w-md rounded-2xl border border-mld-panel-border bg-white text-left shadow-xl">
                                <div className="border-b border-mld-panel-border px-6 py-4">
                                    <Dialog.Title className="text-lg font-semibold text-mld-menu-text">
                                        Add Provider
                                    </Dialog.Title>
                                    <p className="text-xs text-mld-muted-text">
                                        NPI is optional — if left blank a synthetic NPI will be generated.
                                    </p>
                                </div>
                                <div className="space-y-4 px-6 py-5">
                                    <div>
                                        <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                            Provider Name
                                        </label>
                                        <input
                                            type="text"
                                            value={orgConsoleAddProviderName}
                                            onChange={(event) => {
                                                setOrgConsoleAddProviderName(event.target.value);
                                                setOrgConsoleAddProviderMessage({type: '', text: ''});
                                            }}
                                            placeholder="e.g. Main Office"
                                            className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                            NPI (optional)
                                        </label>
                                        <input
                                            type="text"
                                            value={orgConsoleAddProviderNpi}
                                            onChange={(event) => {
                                                setOrgConsoleAddProviderNpi(event.target.value);
                                                setOrgConsoleAddProviderMessage({type: '', text: ''});
                                            }}
                                            placeholder="10-digit NPI"
                                            className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                        />
                                    </div>
                                    {orgConsoleAddProviderMessage.text ? (
                                        <div
                                            className={clsx(
                                                'text-sm',
                                                orgConsoleAddProviderMessage.type === 'error'
                                                    ? 'text-mld-danger'
                                                    : 'text-mld-success'
                                            )}>
                                            {orgConsoleAddProviderMessage.text}
                                        </div>
                                    ) : null}
                                </div>
                                <div className="flex items-center justify-end gap-3 border-t border-mld-panel-border px-6 py-4">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setOrgConsoleAddProviderOpen(false);
                                            setOrgConsoleAddProviderName('');
                                            setOrgConsoleAddProviderNpi('');
                                            setOrgConsoleAddProviderMessage({type: '', text: ''});
                                        }}
                                        className="rounded-md border border-mld-panel-border px-3 py-2 text-sm text-mld-text-secondary hover:bg-mld-menu-background">
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleAddProviderSubmit}
                                        disabled={orgConsoleAddProviderSaving}
                                        className="inline-flex items-center justify-center gap-2 rounded-md bg-mld-primary px-4 py-2 text-sm text-white hover:bg-mld-primary-700 disabled:opacity-60">
                                        {orgConsoleAddProviderSaving ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : null}
                                        Add Provider
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </div>
                    </div>
                </Dialog>

                <OhdLabelMapping
                    open={ohdMappingModalOpen}
                    onClose={() => setOhdMappingModalOpen(false)}
                    orgId={tenantOrganization?.id ?? null}
                    ohdOrgId={ohdOrganizationId ?? null}
                />
            </AdminNotificationProvider>
        );
    }

    return (
        <AdminNotificationProvider>
            <div className="flex h-screen bg-mld-background">
                <aside className="flex h-screen w-[280px] flex-col border-r border-mld-panel-border bg-white">
                    <div className="flex h-14 items-center gap-2 border-b border-mld-panel-border px-4">
                        <div className="text-sm font-semibold tracking-tight text-mld-menu-text">
                            myDX<span className="text-mld-primary">direct</span>
                        </div>
                    </div>

                    {/* Sidebar content scrolls only within organization list. */}
                    <div className="flex min-h-0 flex-1 flex-col">
                        <div className="border-b border-mld-panel-border px-3 py-3">
                            <button
                                type="button"
                                onClick={() => handleNavChange('global')}
                                className={clsx(
                                    'mb-1 flex w-full items-center gap-2 rounded-[7px] px-3 py-2 text-xs font-semibold transition',
                                    activePage === 'global'
                                        ? 'bg-mld-primary/10 text-mld-primary'
                                        : 'text-mld-muted-text hover:bg-mld-background hover:text-mld-menu-text'
                                )}>
                                <LayoutDashboard className="h-4 w-4" />
                                Global Dashboard
                            </button>
                            <button
                                type="button"
                                onClick={() => handleNavChange('lab-pricing')}
                                className={clsx(
                                    'mb-1 flex w-full items-center gap-2 rounded-[7px] px-3 py-2 text-xs font-semibold transition',
                                    activePage === 'lab-pricing'
                                        ? 'bg-mld-primary/10 text-mld-primary'
                                        : 'text-mld-muted-text hover:bg-mld-background hover:text-mld-menu-text'
                                )}>
                                <FlaskConical className="h-4 w-4" />
                                Lab Pricing
                            </button>
                            <button
                                type="button"
                                onClick={() => handleNavChange('biomatch')}
                                className={clsx(
                                    'mb-1 flex w-full items-center gap-2 rounded-[7px] px-3 py-2 text-xs font-semibold transition',
                                    activePage === 'biomatch'
                                        ? 'bg-mld-primary/10 text-mld-primary'
                                        : 'text-mld-muted-text hover:bg-mld-background hover:text-mld-menu-text'
                                )}>
                                <Sparkles className="h-4 w-4" />
                                BioMatch
                            </button>
                            <button
                                type="button"
                                onClick={() => handleNavChange('global-products')}
                                className={clsx(
                                    'mb-1 flex w-full items-center gap-2 rounded-[7px] px-3 py-2 text-xs font-semibold transition',
                                    activePage === 'global-products'
                                        ? 'bg-mld-primary/10 text-mld-primary'
                                        : 'text-mld-muted-text hover:bg-mld-background hover:text-mld-menu-text'
                                )}>
                                <ShoppingBag className="h-4 w-4" />
                                Global Products
                            </button>
                            <button
                                type="button"
                                onClick={() => handleNavChange('kit-costs')}
                                className={clsx(
                                    'mb-1 flex w-full items-center gap-2 rounded-[7px] px-3 py-2 text-xs font-semibold transition',
                                    activePage === 'kit-costs'
                                        ? 'bg-mld-primary/10 text-mld-primary'
                                        : 'text-mld-muted-text hover:bg-mld-background hover:text-mld-menu-text'
                                )}>
                                <Package className="h-4 w-4" />
                                Kit Costs
                            </button>
                            <button
                                type="button"
                                onClick={() => handleNavChange('biomarker-display-rules')}
                                className={clsx(
                                    'mb-1 flex w-full items-center gap-2 rounded-[7px] px-3 py-2 text-xs font-semibold transition',
                                    activePage === 'biomarker-display-rules'
                                        ? 'bg-mld-primary/10 text-mld-primary'
                                        : 'text-mld-muted-text hover:bg-mld-background hover:text-mld-menu-text'
                                )}>
                                <SlidersHorizontal className="h-4 w-4" />
                                Biomarkers
                            </button>
                            <button
                                type="button"
                                onClick={() => handleNavChange('orders')}
                                className={clsx(
                                    'mb-1 flex w-full items-center gap-2 rounded-[7px] px-3 py-2 text-xs font-semibold transition',
                                    activePage === 'orders'
                                        ? 'bg-mld-primary/10 text-mld-primary'
                                        : 'text-mld-muted-text hover:bg-mld-background hover:text-mld-menu-text'
                                )}>
                                <ShoppingBag className="h-4 w-4" />
                                Orders
                            </button>
                            <button
                                type="button"
                                onClick={() => handleNavChange('coupons')}
                                className={clsx(
                                    'mb-1 flex w-full items-center gap-2 rounded-[7px] px-3 py-2 text-xs font-semibold transition',
                                    activePage === 'coupons'
                                        ? 'bg-mld-primary/10 text-mld-primary'
                                        : 'text-mld-muted-text hover:bg-mld-background hover:text-mld-menu-text'
                                )}>
                                <Tag className="h-4 w-4" />
                                Coupons
                            </button>
                            <button
                                type="button"
                                onClick={handleSpotlightOpen}
                                className="mb-1 flex w-full items-center gap-2 rounded-[7px] px-3 py-2 text-xs font-semibold text-mld-muted-text transition hover:bg-mld-background hover:text-mld-menu-text">
                                <Search className="h-4 w-4" />
                                Find Org
                                <span className="ml-auto rounded border border-mld-panel-border bg-mld-background px-1.5 py-0.5 text-[9px] text-mld-muted-text">
                                    ⌘K
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={handlePatientSearchOpen}
                                className="mb-1 flex w-full items-center gap-2 rounded-[7px] px-3 py-2 text-xs font-semibold text-mld-muted-text transition hover:bg-mld-background hover:text-mld-menu-text">
                                <Users className="h-4 w-4" />
                                Patient Search
                            </button>
                            <button
                                type="button"
                                onClick={() => handleNavChange('password-resets')}
                                className={clsx(
                                    'mb-1 flex w-full items-center gap-2 rounded-[7px] px-3 py-2 text-xs font-semibold transition',
                                    activePage === 'password-resets'
                                        ? 'bg-mld-primary/10 text-mld-primary'
                                        : 'text-mld-muted-text hover:bg-mld-background hover:text-mld-menu-text'
                                )}>
                                <KeyRound className="h-4 w-4" />
                                Password Resets
                            </button>
                            {userInfo?.email === 'derrick@blueaxis.com' && (
                                <button
                                    type="button"
                                    onClick={() => handleNavChange('fortis-integration')}
                                    className={clsx(
                                        'mb-1 flex w-full items-center gap-2 rounded-[7px] px-3 py-2 text-xs font-semibold transition',
                                        activePage === 'fortis-integration'
                                            ? 'bg-mld-primary/10 text-mld-primary'
                                            : 'text-mld-muted-text hover:bg-mld-background hover:text-mld-menu-text'
                                    )}>
                                    <CreditCard className="h-4 w-4" />
                                    Merchant Integration
                                </button>
                            )}
                        </div>

                        <div className="px-3 py-3">
                            <div className="flex items-center gap-2 rounded-[7px] border border-mld-panel-border bg-mld-background px-3 py-1 text-xs text-mld-muted-text">
                                <Search className="h-3 w-3" />
                                <input
                                    value={orgFilter}
                                    onChange={(event) => setOrgFilter(event.target.value)}
                                    placeholder="Search orgs..."
                                    className="w-full border-none bg-transparent text-xs text-mld-menu-text placeholder:text-mld-muted-text focus:outline-none"
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-between px-4 pb-1">
                            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-mld-muted-text">
                                Organizations ({organizations.length.toLocaleString('en-US')})
                            </span>
                            <button
                                type="button"
                                onClick={() => setAddOrgModalOpen(true)}
                                className="inline-flex items-center gap-1 rounded-md border border-mld-panel-border px-2 py-1 text-[10px] font-semibold text-mld-muted-text hover:text-mld-menu-text">
                                <Plus className="h-3 w-3" />
                                Add
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto px-2 pb-3">
                            {organizationsQuery.isLoading ? (
                                <div className="px-3 py-4 text-xs text-mld-muted-text">Loading organizations...</div>
                            ) : filteredOrganizations.length === 0 ? (
                                <div className="px-3 py-4 text-xs text-mld-muted-text">No organizations found.</div>
                            ) : (
                                filteredOrganizations.map((org) => {
                                    const isActive = activePage === 'org-detail' && selectedOrg?.id === org.id;
                                    const label = org.display_name || org.name;
                                    const providerCount =
                                        org.provider_count == null
                                            ? '—'
                                            : `${org.provider_count.toLocaleString('en-US')} ${org.provider_count === 1 ? 'provider' : 'providers'}`;
                                    return (
                                        <button
                                            key={org.id}
                                            type="button"
                                            onClick={() => handleSelectOrg(org)}
                                            className={clsx(
                                                'mb-1 flex w-full items-center gap-3 rounded-[7px] border-l-2 px-3 py-2 text-left transition',
                                                isActive
                                                    ? 'border-mld-primary bg-mld-primary/10'
                                                    : 'border-transparent hover:bg-mld-background'
                                            )}>
                                            <div
                                                className={clsx(
                                                    'flex h-8 w-8 items-center justify-center rounded-[7px] text-[11px] font-semibold',
                                                    isActive
                                                        ? 'bg-mld-primary text-white'
                                                        : 'bg-mld-background text-mld-muted-text'
                                                )}>
                                                {getInitials(label)}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div
                                                    className={clsx(
                                                        'truncate text-xs font-semibold',
                                                        isActive ? 'text-mld-primary' : 'text-mld-menu-text'
                                                    )}>
                                                    {label}
                                                </div>
                                                <div className="truncate text-[11px] text-mld-muted-text">
                                                    {providerCount}
                                                </div>
                                            </div>
                                            <span
                                                className={clsx(
                                                    'h-2 w-2 rounded-full',
                                                    org.active ? 'bg-mld-status-normal' : 'bg-mld-muted-text'
                                                )}
                                            />
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </aside>

                <main className="flex min-h-0 min-w-0 flex-1 flex-col">
                    <div className="flex h-14 items-center gap-3 border-b border-mld-panel-border bg-white px-6">
                        <div className="flex items-center gap-2 text-sm text-mld-muted-text">
                            {breadcrumbs.map((crumb, index) => (
                                <React.Fragment key={`${crumb}-${index}`}>
                                    <span
                                        className={clsx(
                                            index === breadcrumbs.length - 1 ? 'text-mld-text-secondary' : ''
                                        )}>
                                        {crumb}
                                    </span>
                                    {index < breadcrumbs.length - 1 ? (
                                        <span className="text-mld-muted-text">›</span>
                                    ) : null}
                                </React.Fragment>
                            ))}
                        </div>
                        <div className="ml-auto flex items-center gap-3">
                            <div className="relative" ref={userMenuRef}>
                                <button
                                    type="button"
                                    onClick={() => setUserMenuOpen((prev) => !prev)}
                                    className="flex items-center gap-2 rounded-full px-2.5 py-1.5 text-sm text-mld-text-secondary transition hover:text-mld-menu-text">
                                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-mld-menu-text text-[11px] font-semibold text-white">
                                        {getInitials(userName)}
                                    </div>
                                    <div className="hidden sm:flex flex-col text-left leading-tight">
                                        <span className="text-sm font-semibold text-mld-menu-text">{userName}</span>
                                        <span className="text-[11px] text-mld-muted-text">Super Admin</span>
                                    </div>
                                    <ChevronDown
                                        className={clsx(
                                            'hidden sm:block h-4 w-4 text-mld-muted-text transition-transform',
                                            userMenuOpen ? 'rotate-180' : ''
                                        )}
                                    />
                                </button>
                                {userMenuOpen ? (
                                    <div className="absolute right-0 top-full mt-2 w-40 rounded-lg border border-mld-panel-border bg-white py-1 shadow-lg z-20">
                                        <button
                                            type="button"
                                            onClick={handleLogout}
                                            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold text-mld-danger hover:bg-mld-background">
                                            <LogOut className="h-4 w-4" />
                                            Logout
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto px-7 py-6">
                        <AdminNotificationBanner className="mb-6" />

                        {activePage === 'global' ? <GlobalOverview /> : null}

                        {activePage === 'lab-pricing' ? <LabTestPricing /> : null}

                        {activePage === 'biomatch' ? <BioMatchManager globalScope /> : null}

                        {activePage === 'global-products' ? <StorefrontProductBuilder globalScope /> : null}

                        {activePage === 'kit-costs' ? <KitCostSettings /> : null}

                        {/* One Biomarkers page. 'loinc-codes' stays a valid id so a
                            stale bookmark lands on the reference tab instead of a
                            blank screen. */}
                        {activePage === 'loinc-codes' ? <BiomarkersAdminPage initialTab="reference" /> : null}

                        {activePage === 'biomarker-display-rules' ? <BiomarkersAdminPage /> : null}

                        {activePage === 'coupons' ? <AdminCouponsManager /> : null}

                        {activePage === 'orders' ? <AdminOrdersSection /> : null}

                        {activePage === 'password-resets' ? <PasswordReset /> : null}

                        {activePage === 'fortis-integration' ? <AdminFortisIntegration /> : null}

                        {activePage === 'org-detail' ? (
                            selectedOrg ? (
                                <div className="space-y-6">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex items-center gap-4">
                                            <div className="flex h-12 w-12 items-center justify-center rounded-[11px] bg-gradient-to-br from-mld-primary to-mld-primary text-sm font-semibold text-white">
                                                {getInitials(orgTitle)}
                                            </div>
                                            <div>
                                                <div className="text-lg font-semibold text-mld-menu-text">
                                                    {orgTitle}
                                                </div>
                                                <div className="text-xs text-mld-muted-text">
                                                    {orgDomain ? `${orgDomain} · ` : ''}
                                                    {selectedOrg.active ? 'Active' : 'Inactive'} · {orgProviders}{' '}
                                                    {selectedOrg.provider_count === 1 ? 'provider' : 'providers'}
                                                </div>
                                            </div>
                                            {/* OHD / MDX dashboard mode — driven entirely by the OHD link (Approach B). */}
                                            <OhdModeControl
                                                orgId={selectedOrg.id}
                                                orgName={selectedOrg.name}
                                                features={selectedOrg.features}
                                                allMldOrgs={organizationsQuery.data ?? []}
                                                onChanged={() =>
                                                    queryClient.invalidateQueries({queryKey: ['admin-organizations']})
                                                }
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={openOrgConsole}
                                            className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-gradient-to-br from-[#1a2332] to-[#2d3a4a] px-4 py-2 text-xs font-semibold text-white hover:from-[#243044] hover:to-[#3a4a5e]">
                                            Open Org Console
                                            <ExternalLink className="h-3 w-3 opacity-70" />
                                        </button>
                                    </div>

                                    <div className="flex flex-wrap gap-4 border-b border-mld-panel-border">
                                        {(
                                            [
                                                {id: 'modules', label: 'Modules'},
                                                {id: 'providers', label: 'Provider Access'},
                                                {id: 'subdomain', label: 'Subdomain & Logo'},
                                                {id: 'logins', label: 'Organization Users'},
                                                {id: 'tests-catalog', label: 'Lab Tests Catalog'},
                                                {id: 'biomatch', label: 'BioMatch'},
                                                {id: 'ref-ranges', label: 'Reference Ranges'},
                                                {id: 'api-keys', label: 'API Keys'},
                                            ] as Array<{id: OrgDetailTab; label: string}>
                                        ).map((tab) => (
                                            <button
                                                key={tab.id}
                                                type="button"
                                                onClick={() => setOrgDetailTab(tab.id)}
                                                className={clsx(
                                                    'border-b-2 px-3 py-3 text-sm font-semibold transition',
                                                    orgDetailTab === tab.id
                                                        ? 'border-mld-primary text-mld-primary'
                                                        : 'border-transparent text-mld-muted-text hover:text-mld-text-secondary'
                                                )}>
                                                {tab.label}
                                            </button>
                                        ))}
                                    </div>

                                    {orgDetailTab === 'tests-catalog' ? (
                                        <OrganizationCatalog orgId={selectedOrg.id} />
                                    ) : orgDetailTab === 'biomatch' ? (
                                        <BioMatchManager orgId={selectedOrg.id} />
                                    ) : orgDetailTab === 'ref-ranges' ? (
                                        <ReferenceRangesManager orgId={selectedOrg.id} />
                                    ) : orgDetailTab === 'api-keys' ? (
                                        <ProviderApiKeys orgId={selectedOrg.id} />
                                    ) : (
                                        <OrganizationProviderConsole
                                            selectedOrg={selectedOrg}
                                            showSearch={false}
                                            activeTab={orgDetailTab}
                                            showActionBar
                                            orgFilter={orgFilter}
                                        />
                                    )}
                                </div>
                            ) : (
                                <div className="rounded-xl border border-mld-panel-border bg-white px-6 py-8 text-sm text-mld-muted-text">
                                    Select an organization to view details.
                                </div>
                            )
                        ) : null}
                    </div>
                </main>

                <AdminPatientSearchModal
                    open={patientSearchOpen}
                    mode="global"
                    initialQuery={patientSearchSeed}
                    onClose={() => setPatientSearchOpen(false)}
                    onSelectUser={handlePatientSelect}
                />

                <AddOrganizationModal
                    open={addOrgModalOpen}
                    onClose={() => setAddOrgModalOpen(false)}
                    allMldOrgs={organizationsQuery.data ?? []}
                />

                <Transition appear show={spotlightOpen} as={Fragment}>
                    <Dialog
                        as="div"
                        className="relative z-50"
                        initialFocus={spotlightInputRef}
                        onClose={() => setSpotlightOpen(false)}>
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0">
                            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                        </Transition.Child>

                        <div className="fixed inset-0 overflow-y-auto">
                            <div className="flex min-h-full items-start justify-center px-4 py-16">
                                <Transition.Child
                                    as={Fragment}
                                    enter="ease-out duration-200"
                                    enterFrom="opacity-0 translate-y-2"
                                    enterTo="opacity-100 translate-y-0"
                                    leave="ease-in duration-150"
                                    leaveFrom="opacity-100 translate-y-0"
                                    leaveTo="opacity-0 translate-y-2">
                                    <Dialog.Panel className="w-full max-w-xl rounded-xl border border-mld-panel-border bg-white shadow-xl">
                                        <div className="flex items-center gap-3 border-b border-mld-panel-border px-4 py-3">
                                            <Search className="h-4 w-4 text-mld-muted-text" />
                                            <input
                                                ref={spotlightInputRef}
                                                value={spotlightQuery}
                                                onChange={(event) => setSpotlightQuery(event.target.value)}
                                                placeholder="Search organizations..."
                                                className="flex-1 border-none bg-transparent text-sm text-mld-menu-text focus:outline-none"
                                            />
                                            <span className="rounded border border-mld-panel-border bg-mld-background px-2 py-0.5 text-[10px] text-mld-muted-text">
                                                esc
                                            </span>
                                        </div>
                                        <div className="max-h-[360px] overflow-y-auto">
                                            {spotlightQuery && spotlightResults.length === 0 ? (
                                                <div className="px-4 py-6 text-sm text-mld-muted-text">
                                                    No organizations found.
                                                </div>
                                            ) : (
                                                spotlightResults.map((org) => {
                                                    const label = org.display_name || org.name;
                                                    const providerCount =
                                                        org.provider_count == null
                                                            ? '—'
                                                            : `${org.provider_count.toLocaleString('en-US')} ${org.provider_count === 1 ? 'provider' : 'providers'}`;
                                                    return (
                                                        <button
                                                            key={org.id}
                                                            type="button"
                                                            onClick={() => handleSpotlightSelect(org)}
                                                            className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-mld-text-secondary hover:bg-mld-background">
                                                            <div className="flex h-8 w-8 items-center justify-center rounded-[7px] bg-mld-background text-[11px] font-semibold text-mld-muted-text">
                                                                {getInitials(label)}
                                                            </div>
                                                            <div className="flex-1">
                                                                <div className="text-sm font-semibold text-mld-menu-text">
                                                                    {label}
                                                                </div>
                                                                <div className="text-[11px] text-mld-muted-text">
                                                                    {org.subdomain} · {providerCount}
                                                                </div>
                                                            </div>
                                                            <span
                                                                className={clsx(
                                                                    'rounded-full px-2 py-1 text-[10px] font-semibold',
                                                                    org.active
                                                                        ? 'bg-mld-success/5 text-mld-success'
                                                                        : 'bg-mld-menu-background text-mld-muted-text'
                                                                )}>
                                                                {org.active ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </button>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </Dialog.Panel>
                                </Transition.Child>
                            </div>
                        </div>
                    </Dialog>
                </Transition>
            </div>
        </AdminNotificationProvider>
    );
};

export default Admin;
